import { useRecoverableRead } from '../hooks/useRecoverableRead';
import ReadRecoveryNotice from '../components/ReadRecoveryNotice';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Landmark, CreditCard, ArrowRightLeft, Bell, Construction, Plus, Pencil, Trash2, X, Check, DollarSign, Building2, Users, Briefcase, Search, Power, ArrowDownToLine, ArrowUpFromLine, Calendar, TrendingUp, FileText, Percent, Download, UserCircle , MessageCircle, Zap, RefreshCw, ShieldCheck, AlertTriangle, ChevronDown, ChevronRight, History, FileSpreadsheet, CheckCircle2, Wallet, Copy, BookOpen, Lock, Unlock, ShieldAlert, Smartphone, QrCode, Play, Square, Link2, Send} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import * as XLSX from 'xlsx';
import TotalCoin from './TotalCoin';
import { useRequirePin } from '../components/PinGuard';
import GHWalletCard from '../components/GHWalletCard'; import AndroidNoticesTab from '../components/AndroidNoticesTab';
import OneTouchPayCashoutModal from '../components/OneTouchPayCashoutModal';
import OtpCierreUnitedModal from '../components/OtpCierreUnitedModal';
import OneTouchPayLedgerModal from '../components/OneTouchPayLedgerModal';
import ResumenEjecutivoTab from '../components/ResumenEjecutivoTab';
import OtpSaldosOperacion from '../components/OtpSaldosOperacion';
import { useOtpBankStatus } from '../hooks/useOtpBankStatus';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { id: 'cuentas', label: 'Cuentas', icon: CreditCard },
  { id: 'movimientos', label: 'Movimientos', icon: ArrowRightLeft },
  { id: 'resumen', label: 'Resumen Ejecutivo', icon: FileSpreadsheet },
  { id: 'totalcoin', label: 'TotalCoin', icon: Wallet },
  { id: 'automatizacion', label: 'Automatizacion', icon: Zap },
  { id: 'conexion-bot', label: 'Bot de WhatsApp', icon: Smartphone },
  { id: 'auditoria', label: 'Auditoría', icon: ShieldCheck },
  { id: 'alertas', label: 'Alertas', icon: Bell }, { id: 'android', label: 'Avisos Android', icon: Smartphone },
];
const BANK_COLORS = {
  astropay: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300', accent: '#3b82f6' },
  ppay: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300', accent: '#8b5cf6' },
  uala: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', text: 'text-indigo-700 dark:text-indigo-300', accent: '#6366f1' },
  mercadopago: { bg: 'bg-sky-50 dark:bg-sky-900/20', border: 'border-sky-200 dark:border-sky-800', text: 'text-sky-700 dark:text-sky-300', accent: '#0ea5e9' },
  santander: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300', accent: '#ef4444' },
  galicia: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300', accent: '#f97316' },
  custom: { bg: 'bg-gray-50 dark:bg-gray-800/50', border: 'border-gray-200 dark:border-gray-700', text: 'text-gray-700 dark:text-gray-300', accent: '#6b7280' },
};
const EMPTY_FORM = {
  bank_key: '', bank_name: '', cbu_cvu: '', alias: '', titular: '', owner_name: '',
  initial_balance: 0, commission_rate: 0, expected_amount: 0, carteras: [], clientes: [], status: 'suspended',
};

/* ========== Selector de Dueño con Autocompletado ========== */
function OwnerSelector({ value, onChange, token }) {
  const [owners, setOwners] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/bancos/owners`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setOwners).catch(() => {});
  }, [token]);

  useEffect(() => { setInputValue(value || ''); }, [value]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = owners.filter(o => o.owner_name.toLowerCase().includes(inputValue.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        <UserCircle className="w-4 h-4 inline mr-1" /> Proveedor / Dueño de la cuenta
      </label>
      <input type="text" value={inputValue}
        onChange={e => { setInputValue(e.target.value); onChange(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        placeholder="Nombre del dueño o proveedor..."
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
        data-testid="bank-owner" />
      {showDropdown && (filtered.length > 0 || inputValue) && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(o => (
            <button key={o.owner_name} onClick={() => { onChange(o.owner_name); setInputValue(o.owner_name); setShowDropdown(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-600 last:border-0 flex items-center justify-between"
              data-testid={`owner-option-${o.owner_name}`}>
              <span>{o.owner_name}</span>
              <span className="text-xs text-gray-400">{o.account_count} cuenta{o.account_count > 1 ? 's' : ''}</span>
            </button>
          ))}
          {inputValue && !filtered.find(o => o.owner_name === inputValue) && (
            <div className="px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Nuevo proveedor: "{inputValue}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ========== Formulario de Banco ========== */
function BankForm({ form, setForm, predefined, carteras, clientes, onSave, onCancel, isEditing }) {
  const token = localStorage.getItem('token');
  const [searchCartera, setSearchCartera] = useState('');
  const [searchCliente, setSearchCliente] = useState('');
  const selectBank = (bank) => setForm(f => ({ ...f, bank_key: bank.key, bank_name: bank.name }));
  const toggleItem = (field, id) => setForm(f => ({ ...f, [field]: f[field].includes(id) ? f[field].filter(x => x !== id) : [...f[field], id] }));
  const filteredCarteras = carteras.filter(c => c.name.toLowerCase().includes(searchCartera.toLowerCase()));
  const clientesByCartera = form.carteras.length > 0 ? clientes.filter(c => form.carteras.includes(c.cartera_id)) : clientes;
  const filteredClientes = clientesByCartera.filter(c => c.name.toLowerCase().includes(searchCliente.toLowerCase()));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6 shadow-sm" data-testid="bank-form">
      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-5">{isEditing ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</h3>
      {/* Dueño */}
      <div className="mb-5">
        <OwnerSelector value={form.owner_name} onChange={v => setForm(f => ({ ...f, owner_name: v }))} token={token} />
      </div>
      {/* Selector banco */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Banco</label>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 mb-3">
          {predefined.map(bank => {
            const colors = BANK_COLORS[bank.key] || BANK_COLORS.custom; const selected = form.bank_key === bank.key;
            return (<button key={bank.key} onClick={() => selectBank(bank)} data-testid={`bank-select-${bank.key}`}
              className={`p-3 rounded-lg border-2 text-center text-xs font-semibold transition-all ${selected ? `${colors.border} ${colors.bg} ${colors.text} ring-2 ring-offset-1` : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 text-gray-600 dark:text-gray-400'}`}>
              <Building2 className="w-5 h-5 mx-auto mb-1" style={selected ? { color: colors.accent } : {}} />{bank.name}</button>);
          })}
          <button onClick={() => setForm(f => ({ ...f, bank_key: 'custom', bank_name: '' }))} data-testid="bank-select-custom"
            className={`p-3 rounded-lg border-2 text-center text-xs font-semibold transition-all ${form.bank_key === 'custom' ? 'border-gray-400 bg-gray-100 dark:bg-gray-700 ring-2 ring-offset-1' : 'border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 text-gray-500'}`}>
            <Plus className="w-5 h-5 mx-auto mb-1" /> Otro</button>
        </div>
        {form.bank_key === 'custom' && (
          <input type="text" placeholder="Nombre del banco..." value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" data-testid="bank-custom-name" />)}
      </div>
      {/* Banner informativo para AstroPay */}
      {form.bank_key === 'astropay' && (
        <div className="mb-5 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-700 dark:text-blue-300" data-testid="astropay-pending-banner">
          <p className="font-semibold mb-1">Billetera AstroPay — se creara en estado PENDIENTE.</p>
          <p>Tras guardarla, presiona <strong>Conectar</strong> en la tarjeta para vincular los tokens (bearer, refresh, state, discover, device id) y activar la sesion. CBU y Titular se autocompletan al conectar.</p>
        </div>
      )}
      {/* Campos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CBU / CVU{form.bank_key === 'astropay' && <span className="text-gray-400 font-normal"> (opcional)</span>}</label>
          <input type="text" value={form.cbu_cvu} onChange={e => setForm(f => ({ ...f, cbu_cvu: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder={form.bank_key === 'astropay' ? 'Se autocompleta al conectar' : '0000000000000000000000'} data-testid="bank-cbu" /></div>
        {form.bank_key !== 'astropay' && (
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alias</label>
          <input type="text" value={form.alias} onChange={e => setForm(f => ({ ...f, alias: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder="mi.alias.banco" data-testid="bank-alias" /></div>
        )}
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titular{form.bank_key === 'astropay' && <span className="text-gray-400 font-normal"> (opcional)</span>}</label>
          <input type="text" value={form.titular} onChange={e => setForm(f => ({ ...f, titular: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder={form.bank_key === 'astropay' ? 'Se autocompleta al conectar' : 'Nombre del titular'} data-testid="bank-titular" /></div>
        {form.bank_key !== 'astropay' && (
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Saldo Inicial ($)</label>
          <input type="number" value={form.initial_balance} onChange={e => setForm(f => ({ ...f, initial_balance: parseFloat(e.target.value) || 0 }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder="0" data-testid="bank-balance" /></div>
        )}
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Comision (%)</label>
          <div className="relative"><Percent className="w-4 h-4 absolute right-3 top-2.5 text-gray-400" />
            <input type="number" step="0.1" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder="2.5" data-testid="bank-commission" /></div></div>
        {form.bank_key !== 'astropay' && (
        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto Esperado</label>
          <input type="number" value={form.expected_amount} onChange={e => setForm(f => ({ ...f, expected_amount: parseFloat(e.target.value) || 0 }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" placeholder="500000" data-testid="bank-expected-amount" /></div>
        )}
      </div>
      {/* Carteras */}
      <div className="mb-5"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><Briefcase className="w-4 h-4 inline mr-1" /> Carteras asociadas</label>
        <div className="relative mb-2"><Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input type="text" value={searchCartera} onChange={e => setSearchCartera(e.target.value)} placeholder="Buscar cartera..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100" /></div>
        <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 space-y-1">
          {filteredCarteras.map(c => (<label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
            <input type="checkbox" checked={form.carteras.includes(c.id)} onChange={() => toggleItem('carteras', c.id)} className="rounded border-gray-300" />
            <span className="text-gray-700 dark:text-gray-300">{c.name}</span></label>))}
          {filteredCarteras.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Sin resultados</p>}</div></div>
      {/* Clientes */}
      <div className="mb-5"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><Users className="w-4 h-4 inline mr-1" /> Clientes asociados</label>
        <div className="relative mb-2"><Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input type="text" value={searchCliente} onChange={e => setSearchCliente(e.target.value)} placeholder="Buscar cliente..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100" /></div>
        <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 space-y-1">
          {filteredClientes.map(c => (<label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
            <input type="checkbox" checked={form.clientes.includes(c.id)} onChange={() => toggleItem('clientes', c.id)} className="rounded border-gray-300" />
            <span className="text-gray-700 dark:text-gray-300">{c.name}</span></label>))}
          {filteredClientes.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Sin resultados</p>}</div></div>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" data-testid="bank-form-cancel">Cancelar</button>
        <button onClick={() => onSave(form)} disabled={!form.bank_name && form.bank_key !== 'astropay'}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="bank-form-save">
          <Check className="w-4 h-4" /> {isEditing ? 'Guardar' : (form.bank_key === 'astropay' ? 'Crear Pendiente' : 'Crear Cuenta')}</button></div>
    </div>
  );
}

/* ========== Modal Cashout ========== */
function CashoutModal({ account, clientes, onClose, onSuccess }) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [searchCliente, setSearchCliente] = useState('');
  const [f, setF] = useState({ client_id: '', client_name: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
  const avail = account.clientes?.length > 0 ? clientes.filter(c => account.clientes.includes(c.id)) : clientes;
  const filtered = avail.filter(c => c.name.toLowerCase().includes(searchCliente.toLowerCase()));
  const { requirePin, PinGuardUI } = useRequirePin();
  const handleSubmit = async () => {
    if (!f.amount || parseFloat(f.amount) <= 0) return;
    if (!(await requirePin())) return; // PIN 3131 requerido para salidas
    setLoading(true);
    try { const res = await fetch(`${API_URL}/api/bancos/${account.id}/cashout`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ ...f, amount: parseFloat(f.amount) }) });
      if (res.ok) onSuccess(await res.json()); else { const e = await res.json(); alert(e.detail || 'Error'); }
    } catch (e) { alert('Error'); } setLoading(false);
  };
  const colors = BANK_COLORS[account.bank_key] || BANK_COLORS.custom;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="cashout-modal">
        <div className={`px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl ${colors.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-100 dark:bg-red-900/30"><ArrowDownToLine className="w-5 h-5 text-red-600" /></div>
              <div><h3 className="font-bold text-gray-900 dark:text-gray-100">Cashout</h3><p className="text-xs text-gray-500">{account.bank_name} - ${(account.current_balance||0).toLocaleString('es-AR')}</p></div></div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"><X className="w-5 h-5 text-gray-500" /></button></div></div>
        <div className="px-6 py-5 space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente</label>
            {f.client_name ? (<div className="flex items-center justify-between px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700">
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">{f.client_name}</span>
              <button onClick={() => setF(x => ({ ...x, client_id: '', client_name: '' }))} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button></div>
            ) : (<><div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" value={searchCliente} onChange={e => setSearchCliente(e.target.value)} placeholder="Buscar cliente..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" data-testid="cashout-search-client" /></div>
              {searchCliente && (<div className="mt-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                {filtered.map(c => (<button key={c.id} onClick={() => { setF(x => ({ ...x, client_id: c.id, client_name: c.name })); setSearchCliente(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0">{c.name}</button>))}
                {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">Sin resultados</p>}</div>)}</>)}</div>
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto ($)</label>
            <div className="relative"><DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="number" value={f.amount} onChange={e => setF(x => ({ ...x, amount: e.target.value }))} placeholder="0"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium" data-testid="cashout-amount" /></div></div>
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha</label>
            <div className="relative"><Calendar className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="date" value={f.date} onChange={e => setF(x => ({ ...x, date: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" data-testid="cashout-date" /></div></div>
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripcion (opcional)</label>
            <input type="text" value={f.description} onChange={e => setF(x => ({ ...x, description: e.target.value }))} placeholder="Motivo..."
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" data-testid="cashout-description" /></div></div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading || !f.amount || parseFloat(f.amount) <= 0}
            className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="cashout-submit-btn">
            <ArrowDownToLine className="w-4 h-4" /> {loading ? 'Procesando...' : 'Registrar Cashout'}</button></div></div>
      {PinGuardUI}
    </div>
  );
}

/* ========== Modal Cierre Individual ========== */
function SettlementModal({ account, onClose }) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);

  const fetchPreview = useCallback(async () => {
    if (!startDate || !endDate) return; setLoading(true);
    try { const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await fetch(`${API_URL}/api/bancos/${account.id}/settlement-preview?${p}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setPreview(await res.json());
    } catch (e) {} setLoading(false);
  }, [startDate, endDate, account.id, token]);
  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const downloadPDF = async () => { setDownloading(true);
    try { const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await fetch(`${API_URL}/api/bancos/${account.id}/settlement-pdf?${p}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `liquidacion_${account.bank_name}_${startDate}_${endDate}.pdf`; a.click(); window.URL.revokeObjectURL(url); }
    } catch (e) {} setDownloading(false); };

  const sendWhatsApp = async () => {
    if (!startDate || !endDate) return;
    setSendingWA(true);
    try {
      const res = await fetch(API_URL + '/api/bancos/send-settlement-whatsapp', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }, body: JSON.stringify({ account_id: account.id, start_date: startDate, end_date: endDate }) });
      const data = await res.json();
      if (data.success) alert('Liquidacion enviada por WhatsApp!');
      else alert('Error: ' + (data.detail || data.message || 'Error desconocido'));
    } catch (err) { alert('Error: ' + err.message); }
    setSendingWA(false);
  };

  const colors = BANK_COLORS[account.bank_key] || BANK_COLORS.custom;
  const s = preview?.summary || {}; const movements = preview?.movements || [];
  const fmtDate = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : d; };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="settlement-modal">
        <div className={`px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl ${colors.bg} shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/30"><FileText className="w-5 h-5 text-amber-600" /></div>
              <div><h3 className="font-bold text-gray-900 dark:text-gray-100">Cierre de Ciclo</h3>
                <p className="text-xs text-gray-500">{account.bank_name} - {account.titular || ''} - Comision: {account.commission_rate || 0}%</p></div></div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200"><X className="w-5 h-5 text-gray-500" /></button></div></div>
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="flex gap-3 mb-5">
            <div className="flex-1"><label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div>
            <div className="flex-1"><label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div></div>
          {loading ? <div className="text-center py-8 text-gray-400">Calculando...</div> : preview ? (<>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-5 bg-gray-50 dark:bg-gray-900/30">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><p className="text-xs text-gray-500">Total Cash-in</p><p className="text-lg font-bold text-emerald-600">${(s.total_cashin||0).toLocaleString('es-AR')}</p></div>
                <div><p className="text-xs text-gray-500">Total Cashout</p><p className="text-lg font-bold text-red-600">${(s.total_cashout||0).toLocaleString('es-AR')}</p></div></div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-3 flex items-center justify-between">
                <div><p className="text-xs text-gray-500">Comision ({s.commission_rate||0}% sobre cash-in)</p><p className="text-2xl font-bold text-amber-600">${(s.commission_amount||0).toLocaleString('es-AR')}</p></div>
                <div className="text-right"><p className="text-xs text-gray-400">A liquidar</p><p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{account.titular||'-'}</p></div></div></div>
            {movements.length > 0 && (<div className="mb-4"><h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Detalle ({movements.length})</h4>
              <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg"><table className="w-full text-xs"><thead className="bg-gray-100 dark:bg-gray-700/50 sticky top-0"><tr>
                <th className="px-3 py-2 text-left text-gray-500 font-semibold">Fecha</th><th className="px-3 py-2 text-left text-gray-500 font-semibold">Tipo</th>
                <th className="px-3 py-2 text-left text-gray-500 font-semibold">Cliente</th><th className="px-3 py-2 text-right text-gray-500 font-semibold">Monto</th></tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">{movements.map(m => (<tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-3 py-2 text-gray-600">{fmtDate(m.date)}</td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${m.type==='cashin'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{m.type==='cashin'?'IN':'OUT'}</span></td>
                  <td className="px-3 py-2 text-gray-600 truncate max-w-[120px]">{m.client_name||'-'}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${m.type==='cashin'?'text-emerald-600':'text-red-600'}`}>{m.type==='cashin'?'+':'-'}${(m.amount||0).toLocaleString('es-AR')}</td>
                </tr>))}</tbody></table></div></div>)}</>) : null}</div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={sendWhatsApp} disabled={sendingWA || !movements.length}
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="settlement-wa-btn">
            <MessageCircle className="w-4 h-4" /> {sendingWA ? 'Enviando...' : 'Enviar WhatsApp'}</button>
          <button onClick={downloadPDF} disabled={downloading || !movements.length}
            className="px-5 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2" data-testid="settlement-pdf-btn">
            <Download className="w-4 h-4" /> {downloading ? 'Generando...' : 'Descargar PDF'}</button></div></div></div>
  );
}

/* ========== Modal Cierre Consolidado por Dueño ========== */
function OwnerSettlementModal({ ownerName, onClose }) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);

  const fetchPreview = useCallback(async () => {
    if (!startDate || !endDate) return; setLoading(true);
    try { const p = new URLSearchParams({ owner_name: ownerName, start_date: startDate, end_date: endDate });
      const res = await fetch(`${API_URL}/api/bancos/owner-settlement-preview?${p}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setPreview(await res.json());
    } catch (e) {} setLoading(false);
  }, [startDate, endDate, ownerName, token]);
  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const downloadPDF = async () => { setDownloading(true);
    try { const p = new URLSearchParams({ owner_name: ownerName, start_date: startDate, end_date: endDate });
      const res = await fetch(`${API_URL}/api/bancos/owner-settlement-pdf?${p}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `liquidacion_${ownerName.replace(/ /g,'_')}_${startDate}_${endDate}.pdf`; a.click(); window.URL.revokeObjectURL(url); }
    } catch (e) {} setDownloading(false); };

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const downloadExcel = async () => { setDownloadingExcel(true);
    try { const p = new URLSearchParams({ owner_name: ownerName, start_date: startDate, end_date: endDate });
      const res = await fetch(`${API_URL}/api/bancos/owner-settlement-excel?${p}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `Liquidacion_${ownerName.replace(/ /g,'_')}_${startDate}_al_${endDate}.xlsx`; a.click(); window.URL.revokeObjectURL(url); }
    } catch (e) { console.error(e); } setDownloadingExcel(false); };

  const sendWhatsApp = async () => {
    if (!startDate || !endDate) return;
    setSendingWA(true);
    try {
      const res = await fetch(API_URL + '/api/bancos/send-settlement-whatsapp', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }, body: JSON.stringify({ owner_name: ownerName, start_date: startDate, end_date: endDate }) });
      const data = await res.json();
      if (data.success) alert('Liquidacion enviada por WhatsApp!');
      else alert('Error: ' + (data.detail || data.message || 'Error desconocido'));
    } catch (err) { alert('Error: ' + err.message); }
    setSendingWA(false);
  };

  const gt = preview?.grand_total || {};
  const accounts = preview?.accounts || [];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="owner-settlement-modal">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-amber-50 dark:bg-amber-900/20 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/30"><UserCircle className="w-5 h-5 text-amber-600" /></div>
              <div><h3 className="font-bold text-gray-900 dark:text-gray-100">Cierre Consolidado</h3>
                <p className="text-xs text-gray-500">Proveedor: {ownerName} - {gt.account_count || 0} cuentas</p></div></div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200"><X className="w-5 h-5 text-gray-500" /></button></div></div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="flex gap-3 mb-5">
            <div className="flex-1"><label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div>
            <div className="flex-1"><label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div></div>

          {loading ? <div className="text-center py-8 text-gray-400">Calculando...</div> : preview ? (<>
            {/* Resumen por cuenta */}
            <div className="space-y-3 mb-5">
              {accounts.map(acc => (
                <div key={acc.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/30">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{acc.bank_name}{acc.titular ? ` - ${acc.titular}` : ''}</span>
                    <span className="text-xs text-gray-400">Comision: {acc.commission_rate}%</span></div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-gray-400">Cash-in:</span> <span className="font-bold text-emerald-600">${(acc.total_cashin||0).toLocaleString('es-AR')}</span></div>
                    <div><span className="text-gray-400">Cashout:</span> <span className="font-bold text-red-600">${(acc.total_cashout||0).toLocaleString('es-AR')}</span></div>
                    <div className="text-right"><span className="text-gray-400">Comision:</span> <span className="font-bold text-amber-600">${(acc.commission_amount||0).toLocaleString('es-AR')}</span></div></div></div>
              ))}
            </div>
            {/* Gran Total */}
            <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 p-4 bg-amber-50 dark:bg-amber-900/20 text-center">
              <p className="text-xs text-amber-600 font-medium mb-1">TOTAL A LIQUIDAR A {ownerName.toUpperCase()}</p>
              <p className="text-3xl font-bold text-amber-700 dark:text-amber-300" data-testid="owner-total-commission">${(gt.total_commission||0).toLocaleString('es-AR')}</p>
              <p className="text-xs text-gray-500 mt-1">Sobre ${(gt.total_cashin||0).toLocaleString('es-AR')} en cash-in de {gt.account_count||0} cuentas</p></div>
          </>) : null}</div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={sendWhatsApp} disabled={sendingWA || !accounts.length}
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="owner-settlement-wa-btn">
            <MessageCircle className="w-4 h-4" /> {sendingWA ? 'Enviando...' : 'Enviar WhatsApp'}</button>
          <button onClick={downloadExcel} disabled={downloadingExcel || !accounts.length}
            className="px-5 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-2" data-testid="owner-settlement-excel-btn">
            <Download className="w-4 h-4" /> {downloadingExcel ? 'Generando...' : 'Excel Detallado'}</button>
          <button onClick={downloadPDF} disabled={downloading || !accounts.length}
            className="px-5 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2" data-testid="owner-settlement-pdf-btn">
            <Download className="w-4 h-4" /> {downloading ? 'Generando...' : 'PDF Consolidado'}</button></div></div></div>
  );
}

/* ========== Tab Movimientos ========== */
function MovimientosTab() {
  const [accounts, setAccounts] = useState([]); const [data, setData] = useState({ movements: [], summary: {} });
  const [loading, setLoading] = useState(true); const [selectedBank, setSelectedBank] = useState('');
  const [filterType, setFilterType] = useState(''); const [startDate, setStartDate] = useState(''); const [endDate, setEndDate] = useState('');
  const [editingMov, setEditingMov] = useState(null);
  const [editForm, setEditForm] = useState({});
  const token = localStorage.getItem('token'); const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAccounts = useCallback(async () => { try { const r = await fetch(`${API_URL}/api/bancos`, { headers }); if (r.ok) setAccounts(await r.json()); } catch (e) {} }, []);
  const fetchMovements = useCallback(async () => { setLoading(true);
    try { const p = new URLSearchParams(); if (selectedBank) p.set('bank_account_id', selectedBank); if (filterType) p.set('movement_type', filterType);
      if (startDate) p.set('start_date', startDate); if (endDate) p.set('end_date', endDate); p.set('limit', '200');
      const r = await fetch(`${API_URL}/api/bancos/movements/all?${p}`, { headers }); if (r.ok) setData(await r.json());
    } catch (e) {} setLoading(false); }, [selectedBank, filterType, startDate, endDate]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchMovements(); }, [fetchMovements]);

  const handleDelete = async (mov) => {
    const tipo = mov.type === 'cashin' ? 'Cash-in' : 'Cashout';
    if (!window.confirm(`Eliminar ${tipo} de $${mov.amount?.toLocaleString('es-AR')}?\nEsto revertira el saldo del banco.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/bancos/movements/${mov.id}`, { method: 'DELETE', headers });
      if (r.ok) fetchMovements();
      else { const e = await r.json(); alert(e.detail || 'Error'); }
    } catch (e) { alert('Error de conexion'); }
  };

  const startEdit = (mov) => {
    setEditingMov(mov.id);
    setEditForm({ amount: mov.amount, client_name: mov.client_name || '', description: mov.description || '', date: mov.date || '' });
  };

  const saveEdit = async (movId) => {
    try {
      const r = await fetch(`${API_URL}/api/bancos/movements/${movId}`, {
        method: 'PUT', headers, body: JSON.stringify({ ...editForm, amount: parseFloat(editForm.amount) }),
      });
      if (r.ok) { setEditingMov(null); fetchMovements(); }
      else { const e = await r.json(); alert(e.detail || 'Error'); }
    } catch (e) { alert('Error'); }
  };

  const summary = data.summary || {}; const movements = data.movements || [];
  const fmtDate = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : d; };

  return (
    <div data-testid="movimientos-tab">
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Banco</label>
          <select value={selectedBank} onChange={e => setSelectedBank(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm min-w-[160px]">
            <option value="">Todos</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.bank_name}</option>)}</select></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm">
            <option value="">Todos</option><option value="cashin">Cash-in</option><option value="cashout">Cashout</option></select></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" /></div>
        {(selectedBank||filterType||startDate||endDate) && (<button onClick={() => { setSelectedBank(''); setFilterType(''); setStartDate(''); setEndDate(''); }}
          className="px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 border border-gray-200"><X className="w-4 h-4" /></button>)}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowUpFromLine className="w-4 h-4 text-emerald-600" /><span className="text-xs font-medium text-emerald-600">Total Cash-in</span></div>
          <p className="text-2xl font-bold text-emerald-700">${(summary.total_cashin||0).toLocaleString('es-AR')}</p><p className="text-xs text-emerald-500 mt-0.5">{summary.count_cashin||0} movs</p></div>
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowDownToLine className="w-4 h-4 text-red-600" /><span className="text-xs font-medium text-red-600">Total Cashout</span></div>
          <p className="text-2xl font-bold text-red-700">${(summary.total_cashout||0).toLocaleString('es-AR')}</p><p className="text-xs text-red-500 mt-0.5">{summary.count_cashout||0} movs</p></div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 p-4">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-blue-600" /><span className="text-xs font-medium text-blue-600">Neto</span></div>
          <p className={`text-2xl font-bold ${(summary.net||0)>=0?'text-blue-700':'text-red-700'}`}>${(summary.net||0).toLocaleString('es-AR')}</p><p className="text-xs text-blue-500 mt-0.5">{summary.count_total||0} totales</p></div></div>
      {loading ? <div className="text-center py-12 text-gray-400">Cargando...</div>
       : movements.length === 0 ? (<Card className="border-dashed border-2 border-gray-300"><CardContent className="text-center py-12">
          <ArrowRightLeft className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-400 text-sm">No hay movimientos</p></CardContent></Card>)
       : (<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Banco</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Titular</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Receptor</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Monto</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Detalle</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Fuente</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase w-20"></th>
          </tr></thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">{movements.map(m => (
              editingMov === m.id ? (
                <tr key={m.id} className="bg-amber-50 dark:bg-amber-900/10">
                  <td className="px-3 py-2"><input type="date" value={editForm.date} onChange={e => setEditForm(f => ({...f, date: e.target.value}))}
                    className="w-full px-2 py-1 rounded border border-gray-300 text-xs" /></td>
                  <td className="px-3 py-2"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${m.type==='cashin'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>
                    {m.type==='cashin'?'IN':'OUT'}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{m.bank_name||'-'}</td>
                  <td className="px-3 py-2"><input type="text" value={editForm.client_name} onChange={e => setEditForm(f => ({...f, client_name: e.target.value}))}
                    className="w-full px-2 py-1 rounded border border-gray-300 text-xs" placeholder="Cliente" /></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{m.origin_holder||'-'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{m.recipient_name||'-'}</td>
                  <td className="px-3 py-2"><input type="number" value={editForm.amount} onChange={e => setEditForm(f => ({...f, amount: e.target.value}))}
                    className="w-full px-2 py-1 rounded border border-gray-300 text-xs text-right" /></td>
                  <td className="px-3 py-2"><input type="text" value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))}
                    className="w-full px-2 py-1 rounded border border-gray-300 text-xs" placeholder="Detalle" /></td>
                  <td className="px-3 py-2 text-xs text-gray-400">{m.source||'-'}</td>
                  <td className="px-3 py-2 flex gap-1">
                    <button onClick={() => saveEdit(m.id)} className="p-1 rounded hover:bg-emerald-100" title="Guardar" data-testid={`mov-save-${m.id}`}><Check className="w-3.5 h-3.5 text-emerald-600" /></button>
                    <button onClick={() => setEditingMov(null)} className="p-1 rounded hover:bg-gray-200" title="Cancelar"><X className="w-3.5 h-3.5 text-gray-500" /></button>
                  </td>
                </tr>
              ) : (
                <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 group/row">
                  <td className="px-3 py-3 text-gray-700 whitespace-nowrap text-xs">{fmtDate(m.date)}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${m.type==='cashin'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>
                    {m.type==='cashin'?<><ArrowUpFromLine className="w-3 h-3"/>IN</>:<><ArrowDownToLine className="w-3 h-3"/>OUT</>}</span></td>
                  <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{m.bank_name||'-'}</td>
                  <td className="px-3 py-3 text-gray-700 text-xs max-w-[130px] truncate">{m.client_name||'-'}</td>
                  <td className="px-3 py-3 text-gray-500 text-xs max-w-[130px] truncate">{m.origin_holder||'-'}</td>
                  <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{m.recipient_name||'-'}</td>
                  <td className={`px-3 py-3 text-right font-bold whitespace-nowrap text-xs ${m.type==='cashin'?'text-emerald-600':'text-red-600'}`}>{m.type==='cashin'?'+':'-'}${(m.amount||0).toLocaleString('es-AR')}</td>
                  <td className="px-3 py-3 text-gray-500 text-xs max-w-[150px] truncate">{m.description||'-'}</td>
                  <td className="px-3 py-3 text-gray-400 text-xs">{m.source||m.created_by||'-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(m)} className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Editar" data-testid={`mov-edit-${m.id}`}><Pencil className="w-3 h-3 text-blue-500" /></button>
                      <button onClick={() => handleDelete(m)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30" title="Eliminar" data-testid={`mov-delete-${m.id}`}><Trash2 className="w-3 h-3 text-red-500" /></button>
                    </div>
                  </td>
                </tr>
              )
            ))}</tbody></table></div></div>)}
    </div>
  );
}



/* ========== Modal Cashout AstroPay (3 Pasos) ========== */
function AstroPayCashoutModal({ astroLive, accountId, onClose, onSuccess }) {
  const token = localStorage.getItem('token');
  const [step, setStep] = useState(1); // 1=Buscar, 2=Preview, 3=PIN+Ejecutar
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Paso 1 - Busqueda
  const [searchValue, setSearchValue] = useState('');
  const [recipient, setRecipient] = useState(null);
  
  // Paso 2 - Preview
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  
  // Paso 3 - PIN
  const [pin, setPin] = useState('');
  const [result, setResult] = useState(null);

  const arsBalance = astroLive?.wallets?.find(w => w.currency === 'ARS')?.balance || 0;

  // Buscar destinatario
  const handleLookup = async () => {
    if (!searchValue.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/astropay/lookup-cvu`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_account: searchValue.trim(), account_id: accountId })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Destinatario no encontrado'); }
      const data = await res.json();
      setRecipient(data.target_user || data);
      setStep(2);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  // Preview de transferencia (tolerante a fallos - preview es opcional en AstroPay)
  const handlePreview = async () => {
    const monto = parseFloat(amount);
    if (!monto || monto <= 0) { setError('Ingresa un monto valido'); return; }
    if (monto > arsBalance) { setError('Saldo insuficiente'); return; }
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({
        amount: monto,
        cbu: recipient.cbu || '',
        account_id: accountId || '',
      });
      const res = await fetch(`${API_URL}/api/astropay/cvu/preview?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!data.error && data.summary) {
        setPreview(data.summary);
      } else {
        // Preview no disponible - usar datos locales
        setPreview({ origin_amount: monto, target_amount: monto, fee: 0, exchange_rate: 1.0 });
      }
    } catch (e) {
      // Si falla, continuar con datos locales
      setPreview({ origin_amount: parseFloat(amount), target_amount: parseFloat(amount), fee: 0, exchange_rate: 1.0 });
    }
    setStep(3);
    setLoading(false);
  };

  // Ejecutar transferencia
  const handleSend = async () => {
    if (!pin || pin.length < 4) { setError('Ingresa tu PIN de AstroPay'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/astropay/send-cvu`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount),
          cbu: recipient.cbu,
          name: recipient.name,
          cuit: recipient.cuit || '',
          entity: recipient.entity || '',
          alias: recipient.alias || '',
          pin: pin,
          account_id: accountId,
        })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStep(4); // Paso 4 = Resultado exitoso
      } else {
        setError(data.error || 'Error en la transferencia');
      }
    } catch (e) { setError('Error de conexion'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="astropay-cashout-modal">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/50">
                <ArrowUpFromLine className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-100">Transferir AstroPay</h3>
                <p className="text-xs text-gray-500">Saldo: ${arsBalance.toLocaleString('es-AR', {minimumFractionDigits: 2})}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600" data-testid="astropay-cashout-close">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {/* Stepper */}
          {step <= 3 && (
            <div className="flex items-center gap-2 mt-3">
              {[1,2,3].map(s => (
                <div key={s} className="flex items-center gap-1 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
                  <span className={`text-[10px] font-medium ${step >= s ? 'text-blue-700 dark:text-blue-300' : 'text-gray-400'}`}>
                    {s === 1 ? 'Buscar' : s === 2 ? 'Monto' : 'Confirmar'}
                  </span>
                  {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-blue-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* PASO 1: Buscar destinatario */}
        {step === 1 && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alias o CBU/CVU del destinatario</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)}
                  placeholder="ej: mi.alias.cuenta o 0720061..."
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  data-testid="astropay-cashout-search" autoFocus />
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={handleLookup} disabled={loading || !searchValue.trim()}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="astropay-cashout-lookup-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>
        )}

        {/* PASO 2: Monto + Preview */}
        {step === 2 && recipient && (
          <div className="px-6 py-5 space-y-4">
            {/* Info destinatario */}
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-3">
                {recipient.entity_image && <img src={recipient.entity_image} alt="" className="w-8 h-8 rounded" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200 truncate">{recipient.name}</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400">{recipient.entity || 'Entidad desconocida'}</p>
                  <p className="text-[10px] text-gray-500 font-mono truncate">{recipient.cbu}</p>
                  {recipient.alias && <p className="text-[10px] text-gray-400">Alias: {recipient.alias}</p>}
                </div>
                <button onClick={() => { setStep(1); setRecipient(null); setError(''); }} className="p-1 rounded hover:bg-gray-200">
                  <Pencil className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            </div>
            {/* Monto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto a transferir (ARS)</label>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input type="number" value={amount} onChange={e => { setAmount(e.target.value); setError(''); }}
                  placeholder="0.00" min="1" step="0.01"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium"
                  data-testid="astropay-cashout-amount" autoFocus />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Disponible: ${arsBalance.toLocaleString('es-AR', {minimumFractionDigits: 2})}</p>
            </div>
            <div className="flex justify-between">
              <button onClick={() => { setStep(1); setError(''); }} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50">Atras</button>
              <button onClick={handlePreview} disabled={loading || !amount || parseFloat(amount) <= 0}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="astropay-cashout-preview-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                {loading ? 'Consultando...' : 'Continuar'}
              </button>
            </div>
          </div>
        )}

        {/* PASO 3: PIN + Confirmar */}
        {step === 3 && preview && (
          <div className="px-6 py-5 space-y-4">
            {/* Resumen */}
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 space-y-2">
              <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">Resumen de Transferencia</h4>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Destinatario:</span>
                <span className="font-medium text-gray-800 dark:text-gray-200 text-right max-w-[60%] truncate">{recipient.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Entidad:</span>
                <span className="text-gray-700 dark:text-gray-300">{recipient.entity || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">CBU:</span>
                <span className="text-gray-700 dark:text-gray-300 font-mono text-xs">{recipient.cbu}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 my-2" />
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Monto:</span>
                <span className="font-bold text-gray-800 dark:text-gray-200">${parseFloat(amount).toLocaleString('es-AR', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Comision:</span>
                <span className="text-emerald-600 font-medium">${(preview.fee || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span className="text-gray-700 dark:text-gray-300">Total a debitar:</span>
                <span className="text-blue-700 dark:text-blue-300">${((preview.origin_amount || parseFloat(amount)) + (preview.fee || 0)).toLocaleString('es-AR', {minimumFractionDigits: 2})}</span>
              </div>
            </div>
            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PIN de seguridad AstroPay</label>
              <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(''); }}
                placeholder="Ingresa tu PIN"
                maxLength={6}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-center tracking-[0.5em] font-mono"
                data-testid="astropay-cashout-pin" autoFocus />
            </div>
            <div className="flex justify-between">
              <button onClick={() => { setStep(2); setPin(''); setError(''); }} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50">Atras</button>
              <button onClick={handleSend} disabled={loading || !pin}
                className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="astropay-cashout-send-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {loading ? 'Enviando...' : 'Confirmar Transferencia'}
              </button>
            </div>
          </div>
        )}

        {/* PASO 4: Resultado exitoso */}
        {step === 4 && result && (
          <div className="px-6 py-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-emerald-700 dark:text-emerald-300">Transferencia Enviada</h4>
              <p className="text-sm text-gray-500 mt-1">Estado: {result.status || 'PENDING'}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-left space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Monto:</span>
                <span className="font-bold">${parseFloat(amount).toLocaleString('es-AR', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Destinatario:</span>
                <span className="font-medium truncate max-w-[60%] text-right">{result.recipient || recipient?.name}</span>
              </div>
              {result.transfer_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">ID:</span>
                  <span className="font-mono text-xs">{result.transfer_id}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-center gap-3">
              {result.transfer_id && (
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/astropay/invoice-by-reference/${result.transfer_id}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.file_resource) {
                      window.open(data.file_resource, '_blank');
                    } else {
                      alert('Comprobante no disponible aun. Intenta en unos segundos.');
                    }
                  } catch(e) { alert('Error obteniendo comprobante'); }
                }}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-2"
                  data-testid="astropay-cashout-invoice-btn">
                  <FileText className="w-4 h-4" /> Comprobante
                </button>
              )}
              <button onClick={() => { onClose(); if (onSuccess) onSuccess(); }}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                data-testid="astropay-cashout-done-btn">
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========== Modal Conectar AstroPay (Paste-JSON) ========== */
function ConnectAstroPayModal({ accountId, onClose, onSuccess }) {
  const token = localStorage.getItem('token');
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [result, setResult] = useState(null);

  // Parser tolerante: acepta JSON del onboard_collector.py (flat) o nested
  // estilo /v4/auth/refresh + headers.
  function normalize(obj) {
    if (!obj || typeof obj !== 'object') return null;
    const session = obj.session || {};
    const headers = obj.headers || {};
    const user = obj.user || {};
    const wallet = obj.wallet || {};
    const out = {
      bearer_token: obj.bearer_token || session.access_token || headers.authorization?.replace(/^Bearer\s+/i, '') || '',
      refresh_token: obj.refresh_token || session.refresh_token || '',
      state_token: obj.state_token || headers['state-token'] || '',
      discover_token: obj.discover_token || headers['discover-token'] || '',
      device_id: obj.device_id || headers['device-id'] || '',
      rbzsdk: obj.rbzsdk || headers.rbzsdk || '',
      pin: obj.pin || '',
      user_agent: obj.user_agent || headers['user-agent'] || '',
      // Datos meramente informativos (preview)
      _preview: {
        titular: obj.titular || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '(se obtendra del refresh)',
        email: obj.email || (user.email || {}).address || '',
        cvu: obj.cvu || wallet.cvu || '',
        balance: obj.balance ?? wallet.balance ?? null,
      },
    };
    const missing = [];
    if (!out.bearer_token) missing.push('bearer_token');
    if (!out.refresh_token) missing.push('refresh_token');
    if (!out.state_token) missing.push('state_token');
    if (!out.discover_token) missing.push('discover_token');
    if (!out.device_id) missing.push('device_id');
    return { out, missing };
  }

  const onPaste = (text) => {
    setRaw(text);
    setParseError('');
    setParsed(null);
    if (!text.trim()) return;
    try {
      const obj = JSON.parse(text);
      const { out, missing } = normalize(obj);
      if (missing.length) {
        setParseError(`JSON valido pero faltan campos: ${missing.join(', ')}`);
        setParsed({ out, missing });
      } else {
        setParsed({ out, missing: [] });
      }
    } catch (e) {
      setParseError('JSON invalido. Revisa que pegaste el contenido completo entre llaves.');
    }
  };

  const submit = () => {
    if (!parsed || parsed.missing.length) return;
    setLoading(true);
    setServerError('');

    // Uso XMLHttpRequest en vez de fetch para evitar el bug
    // "Failed to execute 'clone' on 'Response'" que dispara algun
    // interceptor del navegador (service worker / extension / devtools).
    // XHR no tiene Response object y no puede ser victima.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/astropay/connect-existing/${accountId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 60000;
    xhr.onload = () => {
      let data = null;
      const text = xhr.responseText || '';
      try { data = text ? JSON.parse(text) : {}; } catch (_) { /* body no JSON */ }
      if (xhr.status >= 200 && xhr.status < 300 && data && data.success) {
        setResult(data);
        setTimeout(() => { onSuccess && onSuccess(data); }, 1500);
      } else {
        const detail = (data && (data.detail || data.message)) ||
                       (text ? text.slice(0, 400) : `HTTP ${xhr.status} (sin body)`);
        setServerError(`HTTP ${xhr.status} - ${detail}`);
      }
      setLoading(false);
    };
    xhr.onerror = () => { setServerError('Error de red (XHR onerror)'); setLoading(false); };
    xhr.ontimeout = () => { setServerError('Timeout. El server tardo mas de 60s.'); setLoading(false); };
    try {
      xhr.send(JSON.stringify(parsed.out));
    } catch (e) {
      setServerError('Error enviando: ' + e.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()} data-testid="astropay-connect-modal">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/40">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100">Conectar billetera AstroPay</h3>
              <p className="text-xs text-gray-500">Pega el JSON generado por el script de captura de la MacBook.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600" data-testid="astropay-connect-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {!result && (<>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">JSON capturado</label>
              <textarea
                value={raw}
                onChange={e => onPaste(e.target.value)}
                placeholder='{"bearer_token":"...","refresh_token":"...","state_token":"...","discover_token":"...","device_id":"...","rbzsdk":"...","pin":"45230500"}'
                className="w-full h-40 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-mono"
                data-testid="astropay-connect-textarea"
              />
              <p className="mt-1 text-[11px] text-gray-500">El parser acepta el output flat del <code>onboard_collector.py</code> o el JSON anidado del /v4/auth/refresh.</p>
            </div>

            {parseError && (
              <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300" data-testid="astropay-connect-parse-error">
                {parseError}
              </div>
            )}

            {parsed && parsed.missing.length === 0 && (
              <div className="px-3 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800" data-testid="astropay-connect-preview">
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">Tokens detectados (4/4) + datos preview:</p>
                <ul className="text-xs text-emerald-700 dark:text-emerald-300 space-y-0.5 font-mono">
                  <li>Titular: {parsed.out._preview.titular}</li>
                  {parsed.out._preview.email && <li>Email:   {parsed.out._preview.email}</li>}
                  {parsed.out._preview.cvu && <li>CVU:     {parsed.out._preview.cvu}</li>}
                  {parsed.out._preview.balance !== null && <li>Saldo:   ${parsed.out._preview.balance.toLocaleString('es-AR')} (preview, se valida en server)</li>}
                  <li>Device:  {parsed.out.device_id.slice(0, 18)}...</li>
                </ul>
              </div>
            )}

            {serverError && (
              <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300" data-testid="astropay-connect-server-error">
                {serverError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" data-testid="astropay-connect-cancel">
                Cancelar
              </button>
              <button onClick={submit} disabled={loading || !parsed || parsed.missing.length > 0}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="astropay-connect-submit">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {loading ? 'Conectando...' : 'Confirmar conexion'}
              </button>
            </div>
          </>)}

          {result && (
            <div className="px-3 py-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-center" data-testid="astropay-connect-success">
              <Check className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <p className="font-bold text-emerald-700 dark:text-emerald-300 mb-2">Cuenta conectada</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">{result.account_name}</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">CVU: {result.cvu}</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">Saldo: ${(result.balance || 0).toLocaleString('es-AR')}</p>
              {result.worker_restart !== 'ok' && (
                <p className="mt-2 text-[10px] text-amber-600">Worker restart: {result.worker_restart}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ========== Modal Cashout 1TouchPay (payout API Partner) ========== */
// ============================================================

/* ========== Modal Conectar / Renovar sesión PPay (Paste-JWT) ========== */
function ConnectPPayModal({ onClose, onSuccess }) {
  const token = localStorage.getItem('token');
  const [jwtToken, setJwtToken] = useState('');
  const [paygilant, setPaygilant] = useState('');
  const [preview, setPreview] = useState(null);
  const [parseError, setParseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [result, setResult] = useState(null);

  // Decodifica el payload del JWT para mostrar usuario + caducidad (preview).
  const onChangeToken = (text) => {
    const t = text.trim();
    setJwtToken(t);
    setParseError('');
    setPreview(null);
    if (!t) return;
    try {
      const parts = t.split('.');
      if (parts.length !== 3) { setParseError('No parece un JWT (deben ser 3 partes separadas por punto).'); return; }
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const expDt = payload.exp ? new Date(payload.exp * 1000) : null;
      const horas = expDt ? Math.round((expDt - new Date()) / 36e5 * 10) / 10 : null;
      setPreview({
        user: payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
        email: payload.email || '',
        expires_at: expDt ? expDt.toLocaleString('es-AR') : '',
        horas,
        expired: horas !== null && horas <= 0,
      });
    } catch (e) {
      setParseError('No se pudo decodificar el JWT. Verifica que pegaste el token completo.');
    }
  };

  const submit = () => {
    if (!jwtToken || parseError) return;
    setLoading(true);
    setServerError('');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/ppay/token`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 60000;
    xhr.onload = () => {
      let data = null;
      const text = xhr.responseText || '';
      try { data = text ? JSON.parse(text) : {}; } catch (_) {}
      if (xhr.status >= 200 && xhr.status < 300 && data && data.success) {
        setResult(data);
        setTimeout(() => { onSuccess && onSuccess(data); }, 1200);
      } else {
        const detail = (data && (data.detail || data.message)) || (text ? text.slice(0, 400) : `HTTP ${xhr.status}`);
        setServerError(`HTTP ${xhr.status} - ${detail}`);
      }
      setLoading(false);
    };
    xhr.onerror = () => { setServerError('Error de red (XHR onerror)'); setLoading(false); };
    xhr.ontimeout = () => { setServerError('Timeout. El server tardó más de 60s.'); setLoading(false); };
    try {
      xhr.send(JSON.stringify({ jwt_token: jwtToken, paygilant_session: paygilant }));
    } catch (e) { setServerError('Error enviando: ' + e.message); setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()} data-testid="ppay-connect-modal">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100 dark:bg-purple-900/40">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100">Conectar / Renovar sesión PPay</h3>
              <p className="text-xs text-gray-500">Pegá el JWT capturado de Personal Pay. La sesión dura 12h.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600" data-testid="ppay-connect-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {!result && (<>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">JWT token (authorization)</label>
              <textarea value={jwtToken} onChange={e => onChangeToken(e.target.value)} rows={5}
                placeholder="eyJ0eXAiOiJKV1QiLCJraWQiOi..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-mono break-all"
                data-testid="ppay-connect-jwt-input" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Paygilant session id <span className="text-gray-400 font-normal">(opcional)</span></label>
              <input value={paygilant} onChange={e => setPaygilant(e.target.value)}
                placeholder="3C18D858-...-65D78E01AD87_1781047211882"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                data-testid="ppay-connect-paygilant-input" />
            </div>

            {parseError && <p className="text-xs text-red-600" data-testid="ppay-connect-parse-error">{parseError}</p>}

            {preview && !parseError && (
              <div className={`p-3 rounded-lg border text-xs ${preview.expired ? 'border-red-200 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'border-purple-200 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'}`} data-testid="ppay-connect-preview">
                <p className="font-semibold mb-1">{preview.expired ? '⚠️ Token EXPIRADO' : '✅ Token válido'}</p>
                {preview.user && <p>Usuario: {preview.user}</p>}
                {preview.email && <p>Email: {preview.email}</p>}
                {preview.expires_at && <p>Expira: {preview.expires_at} {preview.horas !== null && !preview.expired && `(en ${preview.horas}h)`}</p>}
              </div>
            )}

            {serverError && <p className="text-xs text-red-600 break-all" data-testid="ppay-connect-server-error">{serverError}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-testid="ppay-connect-cancel">Cancelar</button>
              <button onClick={submit} disabled={loading || !jwtToken || !!parseError || (preview && preview.expired)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="ppay-connect-submit">
                <Zap className="w-4 h-4" /> {loading ? 'Conectando...' : 'Activar sesión'}
              </button>
            </div>
          </>)}

          {result && (
            <div className="px-3 py-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-center" data-testid="ppay-connect-success">
              <Check className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <p className="font-bold text-emerald-700 dark:text-emerald-300 mb-1">Sesión PPay activada</p>
              {result.session?.user_name && <p className="text-xs text-emerald-700 dark:text-emerald-300">{result.session.user_name}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ========== Tab Cuentas ========== */
function CuentasTab() {
  const bankRead = useRecoverableRead(localStorage.getItem('token'));
  const [accounts, setAccounts] = useState([]); const [predefined, setPredefined] = useState([]);
  const [carteras, setCarteras] = useState([]); const [clientes, setClientes] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true); const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); const [form, setForm] = useState({ ...EMPTY_FORM });
  const [cashoutAccount, setCashoutAccount] = useState(null);
  const [astroCashoutOpen, setAstroCashoutOpen] = useState(null); // guarda {accountId, astroLive} o null
  const [connectAstroOpen, setConnectAstroOpen] = useState(null); // bank_account_id o null
  const [connectPpayOpen, setConnectPpayOpen] = useState(false); // modal conectar/renovar PPay
  const [settlementAccount, setSettlementAccount] = useState(null);
  const [ownerSettlement, setOwnerSettlement] = useState(null);
  const [ppayLive, setPpayLive] = useState(null);
  const [astroLiveMap, setAstroLiveMap] = useState({});
  const { data: otpLive, loading: otpLoading, error: otpError, cycle: otpCycle, refresh: refreshOtp, refreshBooks: refreshOtpBooks } = useOtpBankStatus();
  const [otpCashoutOpen, setOtpCashoutOpen] = useState(false);
  // FASE 1 — Ledger 1TouchPay (libro contable, read-only)
  const [otpLedgerOpen, setOtpLedgerOpen] = useState(false);
  const downloadOtpComprobante = async (otpId) => {
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/comprobante/${otpId}.png`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `comprobante_1touchpay_${otpId}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Comprobante:', e); }
  };
  const [expandedMovCard, setExpandedMovCard] = useState(null);
  const [cardMovements, setCardMovements] = useState({});
  const [movLoading, setMovLoading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fetchAll = useCallback(async () => {
    let loadedAccounts = [];
    try {
      await Promise.all([
        bankRead.get(`${API_URL}/api/bancos`, { headers }).then(r => { loadedAccounts = r.data; setAccounts(r.data); }),
        bankRead.get(`${API_URL}/api/bancos/predefined`, { headers }).then(r => setPredefined(r.data)),
        bankRead.get(`${API_URL}/api/bancos/options/carteras`, { headers }).then(r => setCarteras(r.data)),
        bankRead.get(`${API_URL}/api/bancos/options/clientes`, { headers }).then(r => setClientes(r.data)),
        bankRead.get(`${API_URL}/api/bancos/owners`, { headers }).then(r => setOwners(r.data)),
      ]);
    } catch (e) { /* Recovery notice explains failed reads; preserve loaded data. */ }
    finally { setLoading(false); }
    return loadedAccounts;
  }, [bankRead.get]);
  useEffect(() => { fetchAll().then(accs => { if (accs.length > 0) fetchLiveData(accs); }); }, []);
  const [copiedKey, setCopiedKey] = useState(null);
  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    }).catch(() => {});
  };

  // Fetch datos en vivo de PPay y AstroPay
  const [liveLoading, setLiveLoading] = useState(false);
  const fetchLiveData = useCallback(async (accs) => {
    refreshOtp();
    const accountsList = accs || accounts;
    setLiveLoading(true);
    try {
      const r1 = await fetch(`${API_URL}/api/ppay/status`, { headers });
      if (r1.ok) setPpayLive(await r1.json());
    } catch (e) { console.error('PPay:', e); }
    try {
      const astroAccs = accountsList.filter(a => a.bank_key === 'astropay' && a.astropay_account_id);
      const newMap = {};
      for (const astroAcc of astroAccs) {
        try {
          const r2 = await fetch(`${API_URL}/api/astropay/wallets?account_id=${astroAcc.astropay_account_id}`, { headers });
          if (r2.ok) { newMap[astroAcc.astropay_account_id] = { active: true, ...(await r2.json()) }; }
          else { newMap[astroAcc.astropay_account_id] = { active: false }; }
        } catch (e) { newMap[astroAcc.astropay_account_id] = { active: false }; }
      }
      const noIdAccs = accountsList.filter(a => a.bank_key === 'astropay' && !a.astropay_account_id);
      if (noIdAccs.length > 0) {
        try {
          const r2 = await fetch(`${API_URL}/api/astropay/wallets`, { headers });
          if (r2.ok) { newMap['_default'] = { active: true, ...(await r2.json()) }; }
        } catch (e) {}
      }
      setAstroLiveMap(newMap);
    } catch (e) { console.error('AstroPay:', e); }
    setLiveLoading(false);
  }, [accounts, refreshOtp]);

  // Auto-refresh de saldos en vivo cada 30s (sin apretar Actualizar)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchLiveData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  const loadMovements = useCallback(async (bankKey, astropayAccountId) => {
    const cacheKey = astropayAccountId || bankKey;
    if (cardMovements[cacheKey] && !movLoading) {
      setMovLoading(true);
      try {
        const endpoint = bankKey === 'ppay' ? '/api/ppay/movements' : bankKey === '1touchpay' ? `/api/bancos/1touchpay/wallet-movements${astropayAccountId ? `?cvu_id=${astropayAccountId}` : ''}` : `/api/astropay/movements${astropayAccountId ? `?account_id=${astropayAccountId}` : ''}`;
        const r = await fetch(`${API_URL}${endpoint}`, { headers });
        if (r.ok) {
          const d = await r.json();
          setCardMovements(prev => ({ ...prev, [cacheKey]: d }));
        }
      } catch (e) { console.error(e); }
      setMovLoading(false);
      return;
    }
    setMovLoading(true);
    try {
      const endpoint = bankKey === 'ppay' ? '/api/ppay/movements' : bankKey === '1touchpay' ? `/api/bancos/1touchpay/wallet-movements${astropayAccountId ? `?cvu_id=${astropayAccountId}` : ''}` : `/api/astropay/movements${astropayAccountId ? `?account_id=${astropayAccountId}` : ''}`;
      const r = await fetch(`${API_URL}${endpoint}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCardMovements(prev => ({ ...prev, [cacheKey]: d }));
      }
    } catch (e) { console.error(e); }
    setMovLoading(false);
  }, [cardMovements, movLoading, token]);

  const toggleCardExpansion = (accId, bankKey, astropayAccountId) => {
    if (expandedMovCard === accId) {
      setExpandedMovCard(null);
    } else {
      setExpandedMovCard(accId);
      loadMovements(bankKey, astropayAccountId);
    }
  };

  const handleSave = async (data) => {
    // AstroPay sin tokens vinculados => crear como "pendiente de conexion".
    // No tocamos cuentas que ya estan conectadas (tienen astropay_account_id).
    const payload = { ...data };
    if (payload.bank_key === 'astropay' && !payload.astropay_account_id) {
      payload.status = 'pending_connection';
      if (!payload.bank_name) payload.bank_name = 'AstroPay';
    }
    const url = editingId ? `${API_URL}/api/bancos/${editingId}` : `${API_URL}/api/bancos`;
    try { const r = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
      if (r.ok) { setShowForm(false); setEditingId(null); setForm({ ...EMPTY_FORM }); fetchAll(); }
    } catch (e) {}
  };
  const handleEdit = (acc) => { setForm({ ...acc }); setEditingId(acc.id); setShowForm(true); };
  const [expandedCard, setExpandedCard] = useState(null);
  const [waClients, setWaClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [sendingNotif, setSendingNotif] = useState(false);
  const [notifResult, setNotifResult] = useState(null);

  const toggleExpand = async (acc) => {
    if (expandedCard === acc.id) { setExpandedCard(null); setWaClients([]); setSelectedClients([]); setNotifResult(null); return; }
    setExpandedCard(acc.id); setLoadingClients(true); setSelectedClients([]); setNotifResult(null);
    try {
      const res = await fetch(`${API_URL}/api/bancos/${acc.id}/wa-clients`, { headers });
      if (res.ok) {
        const data = await res.json();
        setWaClients(data.clients || []);
        const defaultMsg = `\u2757\uFE0F *CUENTA ACTIVA*\n\n\uD83C\uDFE6 ${acc.bank_name}\n\uD83D\uDC64 ${acc.titular || ''}${acc.cbu_cvu ? '\n\uD83D\uDCCB CBU: ' + acc.cbu_cvu : ''}${acc.alias ? '\n\uD83D\uDD17 Alias: ' + acc.alias : ''}`;
        setNotifMsg(defaultMsg);
      }
    } catch (e) {}
    setLoadingClients(false);
  };

  const toggleClient = (grupoId) => {
    setSelectedClients(prev => prev.includes(grupoId) ? prev.filter(id => id !== grupoId) : [...prev, grupoId]);
  };

  const toggleAllClients = () => {
    if (selectedClients.length === waClients.length) setSelectedClients([]);
    else setSelectedClients(waClients.map(c => c.grupo_id));
  };

  const sendNotifications = async (accId) => {
    if (selectedClients.length === 0) return;
    setSendingNotif(true); setNotifResult(null);
    try {
      const res = await fetch(`${API_URL}/api/bancos/${accId}/notify-clients`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ grupo_ids: selectedClients, message: notifMsg })
      });
      const data = await res.json();
      if (data.success) {
        setNotifResult({ success: true, total: data.total_groups, seconds: data.estimated_seconds, id: data.notification_id });
      } else {
        setNotifResult({ success: false, error: data.detail || 'Error' });
      }
    } catch (e) { setNotifResult({ success: false, error: e.message }); }
    setSendingNotif(false);
  };

  const [activating, setActivating] = useState(null);
  const [movementsPanel, setMovementsPanel] = useState(null);

  const activateAccount = async (acc) => {
    const count = acc.carteras?.length ? '(se notificaran los grupos de WA)' : '';
    if (!window.confirm('Activar cuenta ' + acc.bank_name + ' - ' + acc.titular + '? Las demas cuentas de las mismas carteras se suspenderan. ' + count)) return;
    setActivating(acc.id);
    try {
      const res = await fetch(API_URL + '/api/bancos/' + acc.id + '/activate', { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (data.success) {
        let msg = 'Cuenta activada!';
        if (data.suspended?.length) msg += ' Suspendidas: ' + data.suspended.join(', ') + '.';
        if (data.notifying_groups > 0) msg += ' Notificando a ' + data.notifying_groups + ' grupos (~' + Math.ceil(data.estimated_seconds / 60) + ' min).';
        alert(msg);
        fetchAll();
      } else { alert('Error: ' + (data.detail || 'Error')); }
    } catch (e) { alert('Error: ' + e.message); }
    setActivating(null);
  };

  const handleToggle = async (acc) => { try { await fetch(`${API_URL}/api/bancos/${acc.id}`, { method: 'PUT', headers, body: JSON.stringify({ status: acc.status === 'active' ? 'suspended' : 'active' }) }); fetchAll(); } catch (e) {} };
  const handleDelete = async (id) => { if (!window.confirm('Eliminar esta cuenta?')) return; try { await fetch(`${API_URL}/api/bancos/${id}`, { method: 'DELETE', headers }); fetchAll(); } catch (e) {} };
  const getCarteraName = (id) => carteras.find(c => c.id === id)?.name || id;
  const getClienteName = (id) => clientes.find(c => c.id === id)?.name || id;

  // Agrupar por dueño
  const grouped = {};
  accounts.forEach(acc => {
    const owner = acc.owner_name || 'Sin proveedor';
    if (!grouped[owner]) grouped[owner] = [];
    grouped[owner].push(acc);
  });

  if (loading && !accounts.length) return <div><ReadRecoveryNotice reads={[bankRead]} /><div className="text-center py-12 text-gray-400">Cargando...</div></div>;

  return (
    <div data-testid="cuentas-tab"><ReadRecoveryNotice reads={[bankRead]} />
      {cashoutAccount && <CashoutModal account={cashoutAccount} clientes={clientes} onClose={() => setCashoutAccount(null)} onSuccess={() => { setCashoutAccount(null); fetchAll(); }} />}
      {astroCashoutOpen && <AstroPayCashoutModal astroLive={astroCashoutOpen.astroLive} accountId={astroCashoutOpen.accountId} onClose={() => setAstroCashoutOpen(null)} onSuccess={() => { setAstroCashoutOpen(null); fetchLiveData(); }} />}
      {otpCashoutOpen && <OneTouchPayCashoutModal otpAcc={otpCashoutOpen} onClose={() => setOtpCashoutOpen(false)} onSuccess={() => { setOtpCashoutOpen(false); refreshOtp({ afterChange: true }); }} />}
      {otpLedgerOpen && <OneTouchPayLedgerModal initialCvuId={typeof otpLedgerOpen === 'number' ? otpLedgerOpen : null} bankAccounts={accounts} clientes={clientes} onBalancesChanged={refreshOtpBooks} onClose={() => setOtpLedgerOpen(false)} />}
      {connectPpayOpen && <ConnectPPayModal onClose={() => setConnectPpayOpen(false)} onSuccess={() => { setConnectPpayOpen(false); fetchLiveData(); }} />}
      {connectAstroOpen && <ConnectAstroPayModal accountId={connectAstroOpen} onClose={() => setConnectAstroOpen(null)} onSuccess={() => { setConnectAstroOpen(null); fetchAll().then(accs => fetchLiveData(accs)); }} />}
      {settlementAccount && (settlementAccount.bank_key === '1touchpay' && ['3771', '3108'].includes((settlementAccount.cbu_cvu || '').slice(-4))
        ? <OtpCierreUnitedModal account={settlementAccount} onClose={() => setSettlementAccount(null)} />
        : <SettlementModal account={settlementAccount} onClose={() => setSettlementAccount(null)} />)}
      {ownerSettlement && <OwnerSettlementModal ownerName={ownerSettlement} onClose={() => setOwnerSettlement(null)} />}

      {!!accounts.some(a => a.bank_key === '1touchpay') && <div className="mb-6"><OtpSaldosOperacion bankState={otpLive} bankAccounts={accounts} bankCycle={otpCycle} bankLoading={otpLoading} bankError={otpError} onRefresh={() => refreshOtp({ afterChange: true })} /></div>}

      {!showForm && (<button onClick={() => { setForm({ ...EMPTY_FORM }); setEditingId(null); setShowForm(true); }}
        className="mb-6 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 shadow-sm" data-testid="bank-new-btn">
        <Plus className="w-4 h-4" /> Nueva Cuenta Bancaria</button>)}
      {showForm && <BankForm form={form} setForm={setForm} predefined={predefined} carteras={carteras} clientes={clientes}
        onSave={handleSave} onCancel={() => { setShowForm(false); setEditingId(null); }} isEditing={!!editingId} />}

      {accounts.length === 0 && !showForm ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-400 text-sm">{bankRead.statuses.length ? 'No se pudieron consultar las cuentas bancarias.' : 'No hay cuentas bancarias'}</p></CardContent></Card>
      ) : (
        <div className="space-y-6" data-testid="bank-accounts-grouped">
          {Object.entries(grouped).map(([ownerName, accs]) => (
            <div key={ownerName}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-gray-400" />
                  <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">{ownerName}</h3>
                  <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{accs.length} cuenta{accs.length > 1 ? 's' : ''}</span>
                </div>
                {ownerName !== 'Sin proveedor' && accs.length >= 1 && (
                  <button onClick={() => setOwnerSettlement(ownerName)}
                    className="text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-700 flex items-center gap-1.5 transition-colors"
                    data-testid={`owner-settlement-${ownerName}`}>
                    <FileText className="w-3.5 h-3.5" /> Cierre Consolidado
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {accs.map(acc => {
                  const colors = BANK_COLORS[acc.bank_key] || BANK_COLORS.custom;
                  return (
                    <div key={acc.id} className={`rounded-xl border ${colors.border} ${colors.bg} p-5 relative group transition-all hover:shadow-md cursor-pointer`} data-testid={`bank-card-${acc.id}`}
                      onClick={() => setMovementsPanel(movementsPanel === acc.id ? null : acc.id)}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.accent + '20' }}>
                            <Building2 className="w-5 h-5" style={{ color: colors.accent }} /></div>
                          <div><h4 className={`font-bold text-sm ${colors.text}`}>{acc.bank_name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${acc.status==='active'?'bg-green-100 text-green-700':acc.status==='pending_connection'?'bg-orange-100 text-orange-700':'bg-amber-100 text-amber-700'}`}>
                              {acc.status==='active'?'Activa':acc.status==='pending_connection'?'Pendiente de conexion':'Suspendida'}</span></div></div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {acc.status === 'active' && (<>
                            <button onClick={(e) => { e.stopPropagation(); toggleExpand(acc); }} className={`p-1.5 rounded-md ${expandedCard === acc.id ? 'bg-emerald-100' : 'hover:bg-emerald-100'}`} title="Notificar" data-testid={`bank-notify-${acc.id}`}><Bell className={`w-3.5 h-3.5 ${expandedCard === acc.id ? 'text-emerald-600' : 'text-emerald-500'}`} /></button>
                            <button onClick={(e) => { e.stopPropagation(); setSettlementAccount(acc); }} className="p-1.5 rounded-md hover:bg-amber-100" title="Cierre" data-testid={`bank-settlement-${acc.id}`}><FileText className="w-3.5 h-3.5 text-amber-600" /></button>
                            <button onClick={(e) => { e.stopPropagation(); setCashoutAccount(acc); }} className="p-1.5 rounded-md hover:bg-red-100" title="Cashout" data-testid={`bank-cashout-${acc.id}`}><ArrowDownToLine className="w-3.5 h-3.5 text-red-500" /></button></>)}
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(acc); }} className="p-1.5 rounded-md hover:bg-white/50" title="Editar" data-testid={`bank-edit-${acc.id}`}><Pencil className="w-3.5 h-3.5 text-gray-500" /></button>
                          {acc.status !== 'active' && acc.carteras?.length > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); activateAccount(acc); }} disabled={activating === acc.id}
                              className="p-1.5 rounded-md hover:bg-emerald-100 disabled:opacity-50" title="Activar y notificar" data-testid={`bank-activate-${acc.id}`}>
                              {activating === acc.id ? <RefreshCw className="w-3.5 h-3.5 text-emerald-500 animate-spin" /> : <Zap className="w-3.5 h-3.5 text-emerald-600" />}</button>)}
                          <button onClick={(e) => { e.stopPropagation(); handleToggle(acc); }} className={`p-1.5 rounded-md ${acc.status==='active'?'hover:bg-amber-100':'hover:bg-green-100'}`} data-testid={`bank-toggle-${acc.id}`}>
                            <Power className={`w-3.5 h-3.5 ${acc.status==='active'?'text-amber-500':'text-green-500'}`} /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(acc.id); }} className="p-1.5 rounded-md hover:bg-red-100" title="Eliminar" data-testid={`bank-delete-${acc.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button></div></div>
                      <div className="mb-3 p-3 rounded-lg bg-white/60 dark:bg-gray-900/30">
                        {acc.bank_key === '1touchpay' && otpLive ? (() => {
                          const otpAcc = otpLive.accounts?.[acc.cbu_cvu];
                          // CVU de salida (309): destino fijo del boton "Enviar a 309". Se busca
                          // dinamicamente para no hardcodear el numero de cuenta.
                          const s309 = Object.entries(otpLive.accounts || {}).find(([, a]) => String(a.cvu_id) === '309');
                          const cvuSalida = s309 ? { cuenta: s309[0], ...s309[1] } : null;
                          const esOut = (otpAcc?.alias || '').includes('out');
                          return (<>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">Saldo 1TouchPay{otpAcc ? ` · CVU ${otpAcc.cvu_id}` : ''}</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${otpAcc ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`otp-live-badge-${acc.id}`}>
                                {otpAcc ? 'EN VIVO' : 'SIN API'}</span></div>
                            <p className="text-xl font-bold text-gray-900 dark:text-gray-100" data-testid={`otp-balance-${acc.id}`}>${((otpAcc?.balance ?? acc.real_balance) || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}</p>
                            {otpAcc?.alias && <p className="text-[10px] text-teal-600 mt-0.5">{otpAcc.alias} · {esOut ? 'CVU de salida' : 'CVU recaudación'} · {otpAcc.titular}</p>}
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              <button onClick={(e) => { e.stopPropagation(); fetchLiveData(); }} disabled={liveLoading} className="text-[10px] px-2 py-0.5 rounded bg-teal-100 hover:bg-teal-200 text-teal-700 font-medium transition-colors disabled:opacity-50" data-testid={`otp-refresh-${acc.id}`}>
                                {liveLoading ? 'Actualizando...' : 'Actualizar saldo'}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); toggleCardExpansion(acc.id, '1touchpay', otpAcc?.cvu_id); }} className="text-[10px] px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors" data-testid={`otp-movs-btn-${acc.id}`}>
                                {expandedMovCard === acc.id ? 'Ocultar movimientos' : 'Ver movimientos'}
                              </button>
                              {otpAcc && (
                                <button onClick={(e) => { e.stopPropagation(); setOtpCashoutOpen({ cvuId: otpAcc.cvu_id, balance: otpAcc.balance, alias: otpAcc.alias }); }} className="text-[10px] px-2 py-0.5 rounded bg-teal-500 hover:bg-teal-600 text-white font-medium transition-colors" data-testid={`otp-transfer-btn-${acc.id}`}>
                                  Transferir
                                </button>
                              )}
                              {/* Traspaso habitual 308 → 309: destino predefinido, solo monto y confirmar. */}
                              {otpAcc && String(otpAcc.cvu_id) === '308' && cvuSalida && (
                                <button onClick={(e) => { e.stopPropagation(); setOtpCashoutOpen({
                                  cvuId: otpAcc.cvu_id, balance: otpAcc.balance, alias: otpAcc.alias,
                                  preset: { cuenta: cvuSalida.alias || cvuSalida.cuenta, alias: cvuSalida.alias, titular: cvuSalida.titular, referencia: 'Traspaso 308-309', skipPin: true, label: 'Enviar 308 → 309' },
                                }); }} className="text-[10px] px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors flex items-center gap-1 whitespace-nowrap" data-testid={`otp-send-309-btn-${acc.id}`}>
                                  <Send className="w-3 h-3" /> Enviar a 309
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); setOtpLedgerOpen(Number(otpAcc?.cvu_id || acc.cvu_id) || true); }} title={`Ver libro contable del CVU ${otpAcc?.cvu_id || acc.cvu_id || ''}`} className="ml-auto text-[10px] px-2 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium transition-colors flex items-center gap-1 whitespace-nowrap" data-testid={`otp-ledger-btn-${acc.id}`}>
                                <BookOpen className="w-3 h-3" /> Ledger CVU {otpAcc?.cvu_id || acc.cvu_id || ''}
                              </button>
                            </div>
                          </>);
                        })() : acc.bank_key === 'ppay' && ppayLive ? (<>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">Saldo PPay</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${ppayLive.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {ppayLive.active ? 'EN VIVO' : 'SIN SESION'}</span></div>
                            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">${(ppayLive.balance || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}</p>
                            {ppayLive.user && <p className="text-[10px] text-purple-500 mt-0.5">{ppayLive.user}</p>}
                            <div className="flex gap-2 mt-1.5">
                              <button onClick={(e) => { e.stopPropagation(); fetchLiveData(); }} disabled={liveLoading} className="text-[10px] px-2 py-0.5 rounded bg-purple-100 hover:bg-purple-200 text-purple-700 font-medium transition-colors disabled:opacity-50">
                                {liveLoading ? 'Actualizando...' : 'Actualizar saldo'}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); toggleCardExpansion(acc.id, 'ppay'); }} className="text-[10px] px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors">
                                {expandedMovCard === acc.id ? 'Ocultar movimientos' : 'Ver movimientos'}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); setConnectPpayOpen(true); }} className="text-[10px] px-2 py-0.5 rounded bg-purple-500 hover:bg-purple-600 text-white font-medium transition-colors flex items-center gap-1" data-testid={`ppay-connect-btn-${acc.id}`}>
                                <Zap className="w-3 h-3" /> {ppayLive.active ? 'Renovar sesión' : 'Conectar'}
                              </button>
                            </div>
                            {ppayLive.active && ppayLive.expires_in_hours != null && (
                              <p className={`text-[10px] mt-1 ${ppayLive.expires_in_hours <= 1 ? 'text-red-500 font-semibold' : 'text-purple-400'}`} data-testid={`ppay-expiry-${acc.id}`}>
                                Sesión vence en {ppayLive.expires_in_hours}h
                              </p>
                            )}
                          </>) : acc.bank_key === 'astropay' && (!acc.astropay_account_id || acc.status === 'pending_connection') ? (<>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">Billetera AstroPay</p>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-orange-100 text-orange-700" data-testid={`astropay-pending-badge-${acc.id}`}>
                                PENDIENTE DE CONEXION</span></div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Sin sesion vinculada. Conecta los tokens para activar el polling y ver saldo en vivo.</p>
                            <div className="flex gap-2 mt-2">
                              <button onClick={(e) => { e.stopPropagation(); setConnectAstroOpen(acc.id); }}
                                className="text-xs px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors flex items-center gap-1.5" data-testid={`astropay-connect-btn-${acc.id}`}>
                                <Zap className="w-3.5 h-3.5" /> Conectar
                              </button>
                            </div>
                          </>) : acc.bank_key === 'astropay' && (astroLiveMap[acc.astropay_account_id] || astroLiveMap['_default']) ? (() => { const astroLive = astroLiveMap[acc.astropay_account_id] || astroLiveMap['_default']; return (<>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">Saldo AstroPay (ARS)</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${astroLive.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {astroLive.active ? 'EN VIVO' : 'SIN SESION'}</span></div>
                            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                              ${(astroLive.wallets?.find(w => w.currency === 'ARS')?.balance || 0).toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                            {astroLive.wallets && <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {astroLive.wallets.filter(w => w.balance > 0 && w.currency !== 'ARS').map(w => (
                                <span key={w.currency} className="text-[10px] px-2 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold">
                                  {w.currency_name || w.currency}: {w.balance.toLocaleString('es-AR', {minimumFractionDigits: 4, maximumFractionDigits: 4})}
                                </span>))}
                              <span className="text-[10px] px-2 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-semibold">
                                Total: ${(astroLive.total_balance?.amount || 0).toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </span>
                            </div>}
                            <div className="flex gap-2 mt-1.5">
                              <button onClick={(e) => { e.stopPropagation(); fetchLiveData(); }} disabled={liveLoading} className="text-[10px] px-2 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium transition-colors disabled:opacity-50">
                                {liveLoading ? 'Actualizando...' : 'Actualizar saldo'}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); toggleCardExpansion(acc.id, 'astropay', acc.astropay_account_id); }} className="text-[10px] px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors">
                                {expandedMovCard === acc.id ? 'Ocultar movimientos' : 'Ver movimientos'}
                              </button>
                              {astroLive.active && (
                                <button onClick={(e) => { e.stopPropagation(); setAstroCashoutOpen({ accountId: acc.astropay_account_id, astroLive: astroLive }); }} className="text-[10px] px-2 py-0.5 rounded bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors" data-testid="astropay-transfer-btn">
                                  Transferir
                                </button>
                              )}
                            </div>
                          </>); })() : (<>
                            <p className="text-xs text-gray-500 mb-0.5">{acc.real_balance ? 'Saldo banco' : 'Saldo actual'}</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">${(acc.real_balance || acc.current_balance || 0).toLocaleString('es-AR')}</p>
                          </>)}
                        <div className="flex gap-3 mt-1">
                          {acc.initial_balance > 0 && <p className="text-xs text-gray-400">Inicial: ${acc.initial_balance.toLocaleString('es-AR')}</p>}
                          {acc.commission_rate > 0 && <p className="text-xs text-amber-500 font-medium">Comision: {acc.commission_rate}%</p>}
                          {acc.expected_amount > 0 && (<div className="mt-1">
                            <div className="flex justify-between text-xs mb-0.5"><span className="text-gray-400">Meta</span><span className={`font-medium ${(acc.current_balance||0) >= acc.expected_amount * 0.8 ? 'text-red-500' : 'text-emerald-500'}`}>{Math.min(100, Math.round((acc.current_balance||0) / acc.expected_amount * 100))}%</span></div>
                            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${(acc.current_balance||0) >= acc.expected_amount * 0.8 ? 'bg-red-500' : (acc.current_balance||0) >= acc.expected_amount * 0.5 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{width: Math.min(100, Math.round((acc.current_balance||0) / acc.expected_amount * 100)) + '%'}} /></div>
                            <p className="text-xs text-gray-400 mt-0.5">Meta: ${acc.expected_amount.toLocaleString('es-AR')}</p></div>)}</div></div>
                      <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                        {acc.cbu_cvu && <p><span className="font-medium">CBU/CVU:</span> {acc.cbu_cvu}</p>}
                        {acc.alias && <p><span className="font-medium">Alias:</span> {acc.alias}</p>}
                        {acc.titular && <p><span className="font-medium">Titular:</span> {acc.titular}</p>}
                        {(acc.titular || acc.cbu_cvu || acc.alias) && (
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard([acc.titular && `Titular: ${acc.titular}`, acc.cbu_cvu && `CBU/CVU: ${acc.cbu_cvu}`, acc.alias && `Alias: ${acc.alias}`].filter(Boolean).join('\n'), `datos-${acc.id}`); }}
                            className="mt-1 flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 hover:bg-teal-100 dark:bg-gray-700 dark:hover:bg-teal-900/30 text-gray-600 dark:text-gray-300 hover:text-teal-700 font-medium transition-colors" data-testid={`copy-datos-${acc.id}`}>
                            {copiedKey === `datos-${acc.id}` ? (<><Check className="w-3 h-3 text-emerald-500" /> ¡Copiado!</>) : (<><Copy className="w-3 h-3" /> Copiar datos</>)}
                          </button>
                        )}</div>
                      {(acc.carteras?.length > 0 || acc.clientes?.length > 0) && (
                        <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-600/50">
                          {acc.carteras?.length > 0 && (<div className="flex flex-wrap gap-1 mb-1">{acc.carteras.map(id => (
                            <span key={id} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{getCarteraName(id)}</span>))}</div>)}
                          {acc.clientes?.length > 0 && (<div className="flex flex-wrap gap-1">{acc.clientes.slice(0, 5).map(id => (
                            <span key={id} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{getClienteName(id)}</span>))}
                            {acc.clientes.length > 5 && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">+{acc.clientes.length - 5}</span>}</div>)}</div>)}

                      {/* Panel de Movimientos PPay/AstroPay/1TouchPay */}
                      {expandedMovCard === acc.id && (acc.bank_key === 'ppay' || acc.bank_key === 'astropay' || acc.bank_key === '1touchpay') && (() => {
                        const movKey = acc.bank_key === 'astropay' ? (acc.astropay_account_id || 'astropay') : acc.bank_key === '1touchpay' ? (otpLive?.accounts?.[acc.cbu_cvu]?.cvu_id || '1touchpay') : acc.bank_key;
                        const movData = cardMovements[movKey];
                        return (
                        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                              Movimientos {movData?.total ? `(${movData.total})` : ''}
                              {movData?.new_count > 0 && <span className="ml-1 text-emerald-600">+{movData.new_count} nuevos</span>}
                            </h4>
                            {movLoading && <span className="text-[10px] text-gray-400 animate-pulse">Cargando...</span>}
                          </div>
                          {acc.bank_key === '1touchpay' && movData?.summary && (
                            <div className="grid grid-cols-4 gap-1.5 mb-2" data-testid={`otp-summary-${acc.id}`}>
                              <div className="px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-900/10 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">Bruto</p>
                                <p className="text-[11px] font-bold text-emerald-600">${(movData.summary.total_bruto || 0).toLocaleString('es-AR')}</p></div>
                              <div className="px-2 py-1 rounded bg-orange-50 dark:bg-orange-900/10 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">Fijo ($30 c/u)</p>
                                <p className="text-[11px] font-bold text-orange-500">-${(movData.summary.total_fijo || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}</p></div>
                              <div className="px-2 py-1 rounded bg-red-50 dark:bg-red-900/10 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">Resto imp.</p>
                                <p className="text-[11px] font-bold text-red-500">-${(movData.summary.total_variable || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}{movData.summary.total_bruto > 0 ? ` (${((movData.summary.total_variable / movData.summary.total_bruto) * 100).toFixed(2)}%)` : ''}</p></div>
                              <div className="px-2 py-1 rounded bg-teal-50 dark:bg-teal-900/10 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">Neto real</p>
                                <p className="text-[11px] font-bold text-teal-600">${(movData.summary.total_neto || 0).toLocaleString('es-AR')}</p></div>
                            </div>
                          )}
                          <div className="max-h-64 overflow-y-auto space-y-1">
                            {acc.bank_key === '1touchpay' && movData?.movements?.map((m, i) => {
                              const isIn = m.tipo === 'ENT';
                              return (
                                <div key={m.otp_id || i} className={`px-2 py-1.5 rounded text-xs ${isIn ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'bg-red-50 dark:bg-red-900/10'}`} data-testid={`otp-mov-${m.otp_id}`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{m.titular || (isIn ? 'Entrada' : 'Salida')}</p>
                                      <p className="text-[10px] text-gray-400">{m.created_at}{m.cuit ? ` · CUIT ${m.cuit}` : ''}{m.estado && m.estado !== 'COMPLETADA' ? ` · ${m.estado}` : ''}</p>
                                    </div>
                                    <div className="text-right ml-2 whitespace-nowrap">
                                      <span className={`font-bold ${isIn ? 'text-emerald-600' : 'text-red-500'}`}>{isIn ? '+' : '-'}${Math.abs(m.importe || 0).toLocaleString('es-AR')}</span>
                                      {isIn && (m.total_descuentos || 0) > 0 && (<p className="text-[10px] text-red-400">fijo -${(m.cargo_fijo ?? 0).toLocaleString('es-AR', {minimumFractionDigits: 2})} · resto -${(m.cargo_variable ?? Math.max((m.total_descuentos || 0) - (m.cargo_fijo ?? 0), 0)).toLocaleString('es-AR', {minimumFractionDigits: 2})} ({m.importe > 0 ? (((m.cargo_variable ?? ((m.total_descuentos || 0) - (m.cargo_fijo ?? 0))) / m.importe) * 100).toFixed(2) : '0.00'}%)</p>)}
                                      {isIn && (<p className="text-[10px] font-semibold text-teal-600">neto ${((m.neto_acreditado ?? m.importe) || 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}</p>)}
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); downloadOtpComprobante(m.otp_id); }} className="ml-1.5 p-1.5 rounded hover:bg-teal-100 dark:hover:bg-teal-900/30 text-teal-600 flex-shrink-0 transition-colors" title="Descargar comprobante PNG" data-testid={`otp-receipt-btn-${m.otp_id}`}>
                                      <Download className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {acc.bank_key === 'ppay' && cardMovements.ppay?.movements?.map((m, i) => (
                              <div key={m.transactionId || i} className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${m.amount_symbol === '+' ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{m.description}</p>
                                  <p className="text-[10px] text-gray-400">{m.formattedDate}</p>
                                </div>
                                <span className={`font-bold ml-2 whitespace-nowrap ${m.amount_symbol === '+' ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {m.amount_formatted || `${m.amount_symbol}$${Math.abs(m.amount_value || 0).toLocaleString('es-AR')}`}
                                </span>
                              </div>
                            ))}
                            {acc.bank_key === 'astropay' && movData?.movements?.map((m, i) => {
                              const isIn = m.amount > 0;
                              const dt = m.date > 1000000000000 ? new Date(m.date) : new Date(m.date * 1000);
                              const dateStr = dt.toLocaleDateString('es-AR', {day:'2-digit', month:'2-digit', year:'numeric'}) + ' ' + dt.toLocaleTimeString('es-AR', {hour:'2-digit', minute:'2-digit'});
                              return (
                                <div key={m.activity_id || i} className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${isIn ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{m.title}</p>
                                    <p className="text-[10px] text-gray-400">{dateStr} - {m.description}</p>
                                  </div>
                                  <span className={`font-bold ml-2 whitespace-nowrap ${isIn ? 'text-emerald-600' : 'text-red-500'}`}>
                                    {isIn ? '+' : ''}{m.currency === 'ARS' ? '$' : ''}{Math.abs(m.amount).toLocaleString('es-AR', {minimumFractionDigits: m.currency === 'ARS' ? 0 : 2})} {m.currency !== 'ARS' ? m.currency : ''}
                                  </span>
                                  {m.invoice_reference_id && m.type === 'TRANSFER_PLATFORM' && (
                                    <button onClick={async (e) => {
                                      e.stopPropagation();
                                      const btn = e.currentTarget;
                                      btn.disabled = true;
                                      try {
                                        const tk = localStorage.getItem('token');
                                        const res = await fetch(`${API_URL}/api/astropay/invoice/${m.invoice_reference_id}`, {
                                          headers: { 'Authorization': `Bearer ${tk}` }
                                        });
                                        const data = await res.json();
                                        if (data.file_resource) {
                                          window.open(data.file_resource, '_blank');
                                        } else {
                                          alert('Comprobante no disponible');
                                        }
                                      } catch(err) { alert('Error obteniendo comprobante'); }
                                      btn.disabled = false;
                                    }}
                                      className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-blue-600 transition-colors"
                                      title="Descargar comprobante"
                                      data-testid={`invoice-btn-${m.invoice_reference_id}`}>
                                      <FileText className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {!movData?.movements?.length && !movLoading && (
                              <p className="text-xs text-gray-400 text-center py-2">Sin movimientos registrados</p>
                            )}
                          </div>
                        </div>
                      ); })()}

                      {expandedCard === acc.id && (
                        <div className="mt-4 pt-4 border-t-2 border-emerald-200 dark:border-emerald-800" data-testid={`notify-panel-${acc.id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Notificar por WhatsApp</h4>
                            {waClients.length > 0 && (
                              <button onClick={toggleAllClients} className="text-xs text-emerald-600 hover:underline" data-testid="toggle-all-clients">
                                {selectedClients.length === waClients.length ? 'Deseleccionar todos' : 'Seleccionar todos'}</button>)}
                          </div>
                          {loadingClients ? <p className="text-xs text-gray-400 py-4 text-center">Cargando clientes...</p> : waClients.length === 0 ? (
                            <p className="text-xs text-gray-400 py-4 text-center">No hay clientes de WhatsApp vinculados a esta cuenta</p>
                          ) : (<>
                            <div className="max-h-52 overflow-y-auto space-y-1 mb-3">
                              {waClients.map(c => (
                                <label key={c.config_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${selectedClients.includes(c.grupo_id) ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border border-transparent'}`} data-testid={`client-checkbox-${c.config_id}`}>
                                  <input type="checkbox" checked={selectedClients.includes(c.grupo_id)} onChange={() => toggleClient(c.grupo_id)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate block">{c.cliente_name}</span>
                                    <span className="text-xs text-gray-400 truncate block">{c.grupo_name || c.grupo_id}</span>
                                  </div>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 shrink-0">{c.cartera_name}</span>
                                </label>))}
                            </div>
                            <div className="mb-3">
                              <label className="text-xs font-medium text-gray-500 mb-1 block">Mensaje</label>
                              <textarea value={notifMsg} onChange={e => setNotifMsg(e.target.value)} rows={4}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 resize-none" data-testid="notify-message" />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">{selectedClients.length} de {waClients.length} seleccionados {selectedClients.length > 0 && `(~${Math.ceil(selectedClients.length * 7 / 60)} min)`}</span>
                              <button onClick={() => sendNotifications(acc.id)} disabled={sendingNotif || selectedClients.length === 0}
                                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 transition-all" data-testid="send-notify-btn">
                                <MessageCircle className="w-4 h-4" /> {sendingNotif ? 'Enviando...' : 'Enviar'}</button>
                            </div>
                            {notifResult && (
                              <div className={`mt-3 p-3 rounded-lg text-xs ${notifResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="notify-result">
                                {notifResult.success ? `Enviando a ${notifResult.total} grupos (~${Math.ceil(notifResult.seconds / 60)} min). Se envian con delay de 7 seg entre cada uno.` : `Error: ${notifResult.error}`}
                              </div>)}
                          </>)}
                        </div>)}
                    </div>);
                })}
              </div>
            </div>
          ))}

        {/* Panel de Movimientos Reales - Ancho Completo */}
        {movementsPanel && (() => {
          const allAccounts = Object.values(grouped).flat();
          const selectedAcc = allAccounts.find(a => a.id === movementsPanel);
          if (!selectedAcc || selectedAcc.bank_key !== 'santander') return null;
          return <RealMovementsPanel key={selectedAcc.id} account={selectedAcc} onClose={() => setMovementsPanel(null)} token={token} onSyncComplete={fetchAll} />;
        })()}
        </div>
      )}<div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"><GHWalletCard /></div>
    </div>
  );
}

/* ========== Panel de Movimientos Reales del Banco ========== */
function RealMovementsPanel({ account, onClose, token, onSyncComplete }) {
  const headers = { 'Authorization': `Bearer ${token}` };
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [realBalance, setRealBalance] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterTitular, setFilterTitular] = useState('');
  const [filterMonto, setFilterMonto] = useState('');
  const [filterType, setFilterType] = useState('all');
  const abortRef = useRef(null);
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 100;

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const fetchStoredMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/worker/movements/${account.id}?limit=50`, { headers });
      if (res.ok) {
        const d = await res.json();
        setMovements(d.movements || []);
      }
    } catch (e) {}
    setLoading(false);
  }, [account.id, token]);

  const saveComment = async (movementId, comment) => {
    try {
      await fetch(`${API_URL}/api/bancos/worker/movements/${movementId}/comment`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment })
      });
      setMovements(prev => prev.map(m => m.id === movementId ? { ...m, comment } : m));
    } catch (e) { console.error('Error saving comment:', e); }
    setEditingComment(null);
    setCommentText('');
  };

  useEffect(() => {
    fetchStoredMovements(0, false);
  }, []);

  const handleSync = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSyncing(true); setSyncResult(null);
    try {
      // Necesitamos el account_name del worker. Lo buscamos por CBU
      const accRes = await fetch(`${API_URL}/api/bancos/worker/accounts`, { headers, signal: controller.signal });
      const accData = await accRes.json();
      const workerAcc = (accData.accounts || []).find(a => a.cbu === account.cbu_cvu);
      if (!workerAcc) { setSyncResult({ error: 'Cuenta no vinculada al worker de Santander' }); setSyncing(false); return; }

      const res = await fetch(`${API_URL}/api/bancos/worker/sync/${workerAcc.name}`, { method: 'POST', headers, signal: controller.signal });
      const data = await res.json();
      if (data.success) {
        setSyncResult({ success: true, newMovs: data.new_movements_inserted, balance: data.real_balance });
        setRealBalance(data.real_balance);
        if (onSyncComplete) onSyncComplete();
        setCurrentSkip(0);
        fetchStoredMovements(0, false);
      } else { setSyncResult({ error: data.detail || 'Error sincronizando' }); }
    } catch (e) { setSyncResult({ error: e.message }); }
    setSyncing(false);
  };

  return (
    <div className="mt-2 mb-4 p-5 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 shadow-lg" data-testid={`movements-panel-${account.id}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-blue-700 dark:text-blue-400 flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4" /> Movimientos Reales</h4>
        <div className="flex items-center gap-2">
          {realBalance !== null && (
            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-full">
              Saldo banco: ${realBalance.toLocaleString('es-AR')}</span>
          )}
          <button onClick={handleSync} disabled={syncing} data-testid={`sync-btn-${account.id}`}
            className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 transition-all">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
          <button onClick={() => setShowFilters(!showFilters)} data-testid={`filter-toggle-${account.id}`}
            className={`p-1.5 rounded-lg transition-all ${showFilters ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
            <Search className="w-4 h-4" /></button>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-4 h-4" /></button>
        </div>
      </div>

      {syncResult && (
        <div className={`mb-3 p-2 rounded-lg text-xs ${syncResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {syncResult.success ? `Sincronizado: ${syncResult.newMovs} movimientos nuevos` : syncResult.error}
        </div>
      )}

      {showFilters && movements.length > 0 && (
        <div className="mb-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2" data-testid="movements-filter-bar">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-gray-400" />
            <input type="text" value={filterTitular} onChange={e => setFilterTitular(e.target.value)} placeholder="Buscar por titular..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-blue-400 focus:border-blue-400" data-testid="filter-titular" />
          </div>
          <div className="relative min-w-[120px]">
            <DollarSign className="w-3.5 h-3.5 absolute left-2.5 top-2 text-gray-400" />
            <input type="number" value={filterMonto} onChange={e => setFilterMonto(e.target.value)} placeholder="Monto..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-blue-400 focus:border-blue-400" data-testid="filter-monto" style={{MozAppearance: 'textfield'}} />
          </div>
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            {[['all','Todos'],['cashin','Entradas'],['cashout','Salidas']].map(([val, label]) => (
              <button key={val} onClick={() => setFilterType(val)} data-testid={`filter-type-${val}`}
                className={`px-2.5 py-1.5 text-xs font-medium transition-all ${filterType === val ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600'}`}>
                {label}</button>
            ))}
          </div>
          {(filterTitular || filterMonto || filterType !== 'all') && (
            <button onClick={() => { setFilterTitular(''); setFilterMonto(''); setFilterType('all'); }} className="px-2 py-1.5 text-xs text-gray-400 hover:text-red-500 transition-all" data-testid="filter-clear">Limpiar</button>
          )}
        </div>
      )}

      {(() => {
        const sorted = [...movements].sort((a, b) => { const da = (a.date || "").split("/").reverse().join("") + (a.hour || "00:00:00").replace(/:/g, ""); const db = (b.date || "").split("/").reverse().join("") + (b.hour || "00:00:00").replace(/:/g, ""); return db.localeCompare(da); });
        const filtered = sorted.filter(m => {
          if (filterType !== 'all' && m.type !== filterType) return false;
          if (filterTitular && !(m.origin_holder || '').toLowerCase().includes(filterTitular.toLowerCase()) && !(m.description || '').toLowerCase().includes(filterTitular.toLowerCase()) && !(m.client_name || '').toLowerCase().includes(filterTitular.toLowerCase())) return false;
          if (filterMonto) { const target = parseFloat(filterMonto); if (!isNaN(target) && Math.abs(m.amount || m.signed_amount || 0) !== target) return false; }
          return true;
        });

        if (loading) return <div className="text-center py-6 text-gray-400 text-xs">Cargando movimientos...</div>;
        if (movements.length === 0) return (
          <div className="text-center py-6">
            <p className="text-gray-400 text-xs mb-2">No hay movimientos sincronizados</p>
            <button onClick={handleSync} disabled={syncing} className="text-xs text-blue-600 hover:underline">
              {syncing ? 'Sincronizando...' : 'Sincronizar ahora'}</button>
          </div>
        );

        return (<div>
          {(filterTitular || filterMonto || filterType !== 'all') && (
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400" data-testid="filter-count">
              Mostrando <span className="font-semibold text-blue-600">{filtered.length}</span> de {movements.length} movimientos
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
              <tr className="text-gray-500">
                <th className="text-left py-2 px-2 font-medium">Fecha</th>
                <th className="text-left py-2 px-2 font-medium">Descripcion</th>
                <th className="text-left py-2 px-2 font-medium">Cliente</th>
                <th className="text-right py-2 px-2 font-medium">Monto</th>
                <th className="text-right py-2 px-2 font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.length === 0 ? (
                <tr><td colSpan="5" className="py-6 text-center text-gray-400 text-xs">No hay movimientos que coincidan con los filtros</td></tr>
              ) : filtered.map((m, i) => (
                <tr key={m.santander_id || i} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50" data-testid={`movement-row-${i}`}>
                  <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{m.date}{m.hour ? ` ${m.hour.slice(0,5)}` : ''}</td>
                  <td className="py-2 px-2 text-gray-700 dark:text-gray-300 truncate max-w-[200px]" title={m.description}>
                    {m.origin_holder && <span className="font-medium">{m.origin_holder} - </span>}
                    {m.description?.split(' - ').pop() || m.description}
                  </td>
                  <td className="py-2 px-2 text-xs whitespace-nowrap">
                    {m.client_name ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium">{m.client_name}</span>
                    ) : editingComment === m.id ? (
                      <div className="flex items-center gap-1">
                        <input type="text" value={commentText} onChange={e => setCommentText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveComment(m.id, commentText); if (e.key === 'Escape') { setEditingComment(null); setCommentText(''); } }}
                          autoFocus placeholder="Agregar nota..."
                          className="w-28 px-1.5 py-0.5 text-xs rounded border border-blue-400 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-blue-400" />
                        <button onClick={() => saveComment(m.id, commentText)} className="text-blue-500 hover:text-blue-700"><Check className="w-3 h-3" /></button>
                        <button onClick={() => { setEditingComment(null); setCommentText(''); }} className="text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ) : m.comment ? (
                      <span onClick={() => { setEditingComment(m.id); setCommentText(m.comment); }}
                        className="px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-medium cursor-pointer hover:bg-amber-100" title="Click para editar">
                        {m.comment}</span>
                    ) : (
                      <span onClick={() => { setEditingComment(m.id); setCommentText(''); }}
                        className="text-gray-300 cursor-pointer hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-1.5 py-0.5 rounded transition-all" title="Click para agregar nota">+</span>
                    )}
                  </td>
                  <td className={`py-2 px-2 text-right font-mono font-medium whitespace-nowrap ${m.type === 'cashin' ? 'text-emerald-600' : 'text-red-500'}`}>
                    {m.type === 'cashin' ? '+' : '-'}${Math.abs(m.amount || m.signed_amount || 0).toLocaleString('es-AR')}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-400 font-mono whitespace-nowrap">
                    {m.balance_after ? `$${m.balance_after.toLocaleString('es-AR')}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {hasMore && (
            <div className="text-center mt-3">
              <button onClick={() => fetchStoredMovements(currentSkip, true)} disabled={loadingMore} data-testid="load-more-movements"
                className="px-4 py-2 text-xs rounded-lg bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50 transition-all border border-gray-200 dark:border-gray-700">
                {loadingMore ? 'Cargando...' : 'Cargar mas movimientos'}
              </button>
            </div>
          )}
        </div>);
      })()}
    </div>
  );
}

/* ========== Tab de Automatizacion Santander ========== */
function AutomatizacionTab() {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [workerHealth, setWorkerHealth] = useState(null);
  const [workerAccounts, setWorkerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiscoverForm, setShowDiscoverForm] = useState(false);
  const [discoverForm, setDiscoverForm] = useState({ name: '', user: '', password: '', chrome_port: 9224 });
  const [discovering, setDiscovering] = useState(false);
  const [discoverResult, setDiscoverResult] = useState(null);
  const [error, setError] = useState(null);
  const refreshInterval = useRef(null);

  const fetchWorkerData = useCallback(async () => {
    try {
      const [hRes, aRes] = await Promise.all([
        fetch(`${API_URL}/api/bancos/worker/health`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/bancos/worker/accounts`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (hRes.ok) setWorkerHealth(await hRes.json());
      if (aRes.ok) { const d = await aRes.json(); setWorkerAccounts(d.accounts || []); }
      setError(null);
    } catch (e) { setError('No se pudo conectar con el worker'); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchWorkerData();
    refreshInterval.current = setInterval(fetchWorkerData, 15000);
    return () => clearInterval(refreshInterval.current);
  }, [fetchWorkerData]);

  const handleDiscover = async () => {
    if (!discoverForm.name || !discoverForm.user || !discoverForm.password) return;
    setDiscovering(true); setDiscoverResult(null); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bancos/worker/accounts/discover`, {
        method: 'POST', headers, body: JSON.stringify(discoverForm)
      });
      let data;
      try { const txt = await res.text(); data = JSON.parse(txt); } catch { data = { detail: 'Error al procesar respuesta del servidor' }; }
      if (res.ok) {
        setDiscoverResult({ success: true, data });
        setShowDiscoverForm(false);
        setDiscoverForm({ name: '', user: '', password: '', chrome_port: 9224 });
        fetchWorkerData();
      } else {
        setDiscoverResult({ success: false, error: data.detail || 'Error ' + res.status });
      }
    } catch (e) { setDiscoverResult({ success: false, error: e.message || 'Error de conexion' }); }
    setDiscovering(false);
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Eliminar la cuenta "${name}" del worker?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/bancos/worker/accounts/${name}`, { method: 'DELETE', headers });
      if (res.ok) fetchWorkerData();
      else { const d = await res.json(); alert(d.detail || 'Error'); }
    } catch (e) { alert(e.message); }
  };

  const handleRediscover = async (name) => {
    if (!window.confirm(`Re-descubrir datos de "${name}"? Se hara login en Santander.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/bancos/worker/accounts/${name}/rediscover`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        alert(`Re-descubrimiento exitoso. ${data.updated_fields?.length ? 'Campos actualizados: ' + data.updated_fields.join(', ') : 'Sin cambios.'}`);
        fetchWorkerData();
      } else { alert(data.detail || 'Error'); }
    } catch (e) { alert(e.message); }
  };

  const handleReload = async () => {
    try {
      await fetch(`${API_URL}/api/bancos/worker/reload`, { method: 'POST', headers });
      fetchWorkerData();
    } catch (e) {}
  };

  // Puertos disponibles (9223-9227)
  const usedPorts = workerAccounts.map(a => a.chrome_port);
  const availablePorts = [9223, 9224, 9225, 9226, 9227].filter(p => !usedPorts.includes(p));

  if (loading) return <div className="text-center py-12 text-gray-400">Conectando con el Worker...</div>;

  return (
    <div data-testid="automatizacion-tab" className="space-y-6">
      {/* Header con estado del worker */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${workerHealth?.status === 'healthy' ? 'bg-emerald-500 animate-pulse' : workerHealth?.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`} />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300" data-testid="worker-status">
            Worker {workerHealth?.status === 'healthy' ? 'Online' : workerHealth?.status === 'degraded' ? 'Degradado' : 'Offline'}
            {workerHealth?.total_accounts !== undefined && ` | ${workerHealth.total_accounts} cuenta(s)`}
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={handleReload} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all" data-testid="worker-reload-btn">
            Recargar</button>
          <button onClick={() => { setShowDiscoverForm(true); setDiscoverResult(null); }} data-testid="add-account-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-all">
            <Plus className="w-4 h-4" /> Nueva Cuenta</button>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm" data-testid="worker-error">{error}</div>}

      {discoverResult && (
        <div className={`p-4 rounded-lg text-sm ${discoverResult.success ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-300'}`} data-testid="discover-result">
          {discoverResult.success ? (
            <div>
              <p className="font-medium">Cuenta descubierta y guardada</p>
              <p className="mt-1">CBU: {discoverResult.data?.account?.cbu} | ID: {discoverResult.data?.account?.account_id}</p>
              {discoverResult.data?.all_discovered_accounts?.length > 1 && (
                <p className="mt-1 text-xs opacity-75">Se encontraron {discoverResult.data.all_discovered_accounts.length} cuentas. Se guardo la primera.</p>
              )}
            </div>
          ) : <p>{discoverResult.error}</p>}
        </div>
      )}

      {/* Formulario de descubrimiento */}
      {showDiscoverForm && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-red-700 dark:text-red-300 flex items-center gap-2">
                <Search className="w-5 h-5" /> Descubrir Cuenta Santander</CardTitle>
              <button onClick={() => setShowDiscoverForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Solo necesitas usuario, contraseña y puerto. El worker descubre el resto automaticamente.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nombre interno</label>
                <input type="text" placeholder="ej: cuenta_maria" value={discoverForm.name}
                  onChange={e => setDiscoverForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" data-testid="discover-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Puerto Chrome</label>
                <select value={discoverForm.chrome_port} onChange={e => setDiscoverForm(f => ({ ...f, chrome_port: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" data-testid="discover-port">
                  {availablePorts.length > 0 ? availablePorts.map(p => <option key={p} value={p}>{p}</option>) : <option disabled>No hay puertos disponibles</option>}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Usuario Santander</label>
                <input type="text" placeholder="Usuario de Home Banking" value={discoverForm.user}
                  onChange={e => setDiscoverForm(f => ({ ...f, user: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" data-testid="discover-user" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Contraseña</label>
                <input type="password" placeholder="Contraseña de Home Banking" value={discoverForm.password}
                  onChange={e => setDiscoverForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" data-testid="discover-password" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowDiscoverForm(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-all">Cancelar</button>
              <button onClick={handleDiscover} disabled={discovering || !discoverForm.name || !discoverForm.user || !discoverForm.password || availablePorts.length === 0}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-all" data-testid="discover-submit-btn">
                {discovering ? (<><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Descubriendo...</>) : (<><Search className="w-4 h-4" /> Descubrir</>)}
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de cuentas del worker */}
      {workerAccounts.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300 dark:border-gray-700">
          <CardContent className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No hay cuentas configuradas</p>
            <p className="text-gray-400 text-sm mt-1">Agrega una cuenta de Santander para comenzar la automatizacion</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {workerAccounts.map(acc => (
            <Card key={acc.name} className={`border transition-all ${acc.chrome_available ? 'border-gray-200 dark:border-gray-700' : 'border-red-300 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10'}`}
              data-testid={`worker-account-${acc.name}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Indicador de estado */}
                    <div className="mt-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${acc.chrome_available ? (acc.cache_valid ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-800') : 'bg-red-100 dark:bg-red-900/30'}`}>
                        <Building2 className={`w-5 h-5 ${acc.chrome_available ? (acc.cache_valid ? 'text-emerald-600' : 'text-gray-400') : 'text-red-500'}`} />
                      </div>
                    </div>
                    {/* Info principal */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{acc.titular || acc.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">Santander</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">{acc.name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>CBU: <span className="font-mono">{acc.cbu}</span></span>
                        <span>Puerto: <span className="font-mono">{acc.chrome_port}</span></span>
                      </div>
                    </div>
                  </div>
                  {/* Botones */}
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleRediscover(acc.name)} title="Re-descubrir" data-testid={`rediscover-${acc.name}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
                      <Search className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(acc.name)} title="Eliminar" data-testid={`delete-worker-${acc.name}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                      <Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {/* Estado */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${acc.chrome_available ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${acc.chrome_available ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    Chrome {acc.chrome_available ? 'OK' : 'Offline'}
                  </span>
                  {acc.cache_valid && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Cache: {acc.cache_size} movs ({acc.cache_age_sec !== null ? Math.floor(acc.cache_age_sec / 60) + ' min' : '?'})
                    </span>
                  )}
                  {acc.in_cooldown && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                      Cooldown: {Math.ceil(acc.cooldown_remaining_sec / 60)} min
                    </span>
                  )}
                  {acc.valid && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Sesion activa
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========== Tab: Auditoría AstroPay ========== */

// Modal con detalle completo de una cuenta: receipts, movs banco, cashouts
function CuentaDetailModal({ cuenta, dateStr, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('receipts');
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (s) => s ? new Date(s).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-';

  if (!cuenta) return null;
  const allReceipts = cuenta.all_receipts || [];
  const allBank = cuenta.all_bank_credits || [];
  const cashouts = cuenta.cashouts_detail || [];

  const matchedReceipts = allReceipts.filter(r => r.matched);
  const unmatchedReceipts = allReceipts.filter(r => !r.matched);
  const matchedBank = allBank.filter(m => m.matched);
  const orphanBank = allBank.filter(m => !m.matched);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="cuenta-detail-modal"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-gray-800 dark:to-gray-800">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              {cuenta.name} <span className="text-sm font-normal text-gray-600 dark:text-gray-400">— {cuenta.titular}</span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              CVU: {cuenta.cvu || '-'} · Saldo actual: <strong>{fmtMoney(cuenta.saldo_actual)}</strong> · Status: <span className={`font-bold ${cuenta.status === 'GREEN' ? 'text-green-600' : cuenta.status === 'YELLOW' ? 'text-yellow-600' : 'text-red-600'}`}>{cuenta.status}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          {[
            { id: 'receipts', label: `📥 Receipts (${allReceipts.length})`, count: allReceipts.length },
            { id: 'bank', label: `🏦 Movs Banco (${allBank.length})`, count: allBank.length },
            { id: 'cashouts', label: `💸 Cashouts (${cashouts.length})`, count: cashouts.length },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-emerald-600 text-emerald-700 bg-white dark:bg-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === 'receipts' && (
            <div>
              <div className="flex gap-4 text-xs mb-3">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded">✓ Matched: {matchedReceipts.length} ({fmtMoney(matchedReceipts.reduce((a, r) => a + (r.amount || 0), 0))})</span>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">⚠ Sin match: {unmatchedReceipts.length} ({fmtMoney(unmatchedReceipts.reduce((a, r) => a + (r.amount || 0), 0))})</span>
              </div>
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b sticky top-0 bg-white dark:bg-gray-900">
                    <th className="text-left py-1.5 px-2">Subido</th>
                    <th className="text-left py-1.5 px-2">Fecha comp.</th>
                    <th className="text-right py-1.5 px-2">Monto</th>
                    <th className="text-left py-1.5 px-2">Origen</th>
                    <th className="text-left py-1.5 px-2">Cliente</th>
                    <th className="text-left py-1.5 px-2">Destino</th>
                    <th className="text-center py-1.5 px-2">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {allReceipts.map(r => (
                    <tr key={r.id} className={`border-b hover:bg-gray-50 dark:hover:bg-gray-800 ${r.matched ? '' : 'bg-yellow-50/50 dark:bg-yellow-900/10'}`} data-testid={`modal-receipt-${r.id}`}>
                      <td className="py-1 px-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="py-1 px-2">
                        {r.date || '-'}
                        {r.lag_days > 0 && (
                          <span className={`ml-1 inline-block px-1 py-0.5 rounded text-[9px] font-bold ${r.lag_days <= 2 ? 'bg-yellow-100 text-yellow-800' : r.lag_days <= 7 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                            +{r.lag_days}d
                          </span>
                        )}
                      </td>
                      <td className="text-right py-1 px-2 font-semibold text-emerald-700">+{fmtMoney(r.amount)}</td>
                      <td className="py-1 px-2 max-w-[160px] truncate" title={r.origin_holder}>{r.origin_holder || '-'}</td>
                      <td className="py-1 px-2 font-medium text-indigo-700">{r.cliente_name || '-'}</td>
                      <td className="py-1 px-2 max-w-[160px] truncate" title={r.destination_holder}>{r.destination_holder || '-'}</td>
                      <td className="text-center py-1 px-2">
                        {r.matched ? (
                          <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">✓ {r.match_quality || 'OK'}</span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-bold" title={r.diagnostic || r.reconciliation_status}>✗ sin match</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {allReceipts.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-4 text-gray-400 italic">Sin receipts en esta ventana</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'bank' && (
            <div>
              <div className="flex gap-4 text-xs mb-3">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded">✓ Asociados a comprobante: {matchedBank.length} ({fmtMoney(matchedBank.reduce((a, m) => a + (m.amount || 0), 0))})</span>
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">⚠ Huérfanos (sin comp.): {orphanBank.length} ({fmtMoney(orphanBank.reduce((a, m) => a + (m.amount || 0), 0))})</span>
              </div>
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b sticky top-0 bg-white dark:bg-gray-900">
                    <th className="text-left py-1.5 px-2">Fecha/Hora</th>
                    <th className="text-right py-1.5 px-2">Monto</th>
                    <th className="text-left py-1.5 px-2">Origen</th>
                    <th className="text-left py-1.5 px-2">Descripción</th>
                    <th className="text-center py-1.5 px-2">Comprobante</th>
                  </tr>
                </thead>
                <tbody>
                  {allBank.map(m => (
                    <tr key={m.id} className={`border-b hover:bg-gray-50 dark:hover:bg-gray-800 ${m.matched ? '' : 'bg-orange-50/50 dark:bg-orange-900/10'}`} data-testid={`modal-bank-${m.id}`}>
                      <td className="py-1 px-2 whitespace-nowrap">{fmtDate(m.date)}</td>
                      <td className="text-right py-1 px-2 font-semibold text-blue-700">+{fmtMoney(m.amount)}</td>
                      <td className="py-1 px-2 max-w-[180px] truncate" title={m.origin_name}>
                        {m.origin_name || '-'}
                        {!m.matched && m.probable_client_name && (
                          <div className="text-[10px] text-indigo-600 font-medium mt-0.5" title={`Coincidió ${m.probable_client_count} vez(es) en los últimos 90 días`}>
                            🎯 Probable: {m.probable_client_name}
                          </div>
                        )}
                        {m.matched && m.matched_outside_window && (
                          <div className="text-[10px] text-cyan-700 font-medium mt-0.5" title={`Comprobante subido el ${fmtDate(m.cw_created_at)}`}>
                            ⏱ Conciliado fuera de ventana · {m.cw_cliente_name || ''}
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-2 text-gray-500 max-w-[280px] truncate" title={m.description}>{m.description || '-'}</td>
                      <td className="text-center py-1 px-2">
                        {m.matched ? (
                          m.matched_outside_window ? (
                            <span className="inline-block px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded text-[10px] font-bold" title="Conciliado con un comprobante subido en otra ventana">✓ otro día</span>
                          ) : (
                            <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">✓ declarado</span>
                          )
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-bold">⚠ sin comprobante</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {allBank.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-4 text-gray-400 italic">Sin movimientos bancarios en esta ventana</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'cashouts' && (
            <CashoutsTable cuenta={cuenta} dateStr={dateStr} onUpdate={onUpdate} />
          )}
        </div>
      </div>
    </div>
  );
}

// Tabla de cashouts con cliente vinculado y comentario editable
function CashoutsTable({ cuenta, dateStr, onUpdate }) {
  const [editingId, setEditingId] = useState(null);
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const startEdit = (cashout) => {
    setEditingId(cashout.id);
    setDraftComment(cashout.admin_comment || '');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraftComment('');
  };
  const saveComment = async (cashout) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/audit/cashout-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          movement_id: cashout.id,
          comment: draftComment,
          client_id: cashout.linked_client_id || null,
          client_name: cashout.linked_client_name || null,
        })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert('Error al guardar: ' + (err.detail || r.statusText));
        return;
      }
      setEditingId(null);
      setDraftComment('');
      if (onUpdate) await onUpdate();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const exportToExcel = () => {
    const rows = (cuenta.cashouts_detail || []).map(m => ({
      Fecha: m.date ? new Date(m.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '-',
      Monto: -Number(m.amount || 0),
      Destino: m.destination_name || '-',
      Cliente: m.linked_client_name || '-',
      Comentario: m.admin_comment || '-',
      Descripcion: m.description || '-',
      MovID: m.id || '',
    }));
    if (!rows.length) return alert('Sin datos para exportar');
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cashouts');
    const safeName = (cuenta.name || 'cuenta').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    XLSX.writeFile(wb, `cashouts_${safeName}_${dateStr}.xlsx`);
  };

  const cashouts = cuenta.cashouts_detail || [];

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={exportToExcel}
          data-testid={`export-cashouts-${cuenta.account_id}`}
          className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-green-50 text-green-700 border-green-300"
        >
          <FileSpreadsheet className="w-3 h-3" /> Excel
        </button>
      </div>
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b">
            <th className="text-left py-1 px-2">Fecha/Hora</th>
            <th className="text-right py-1 px-2">Monto</th>
            <th className="text-left py-1 px-2">Destino</th>
            <th className="text-left py-1 px-2">Cliente</th>
            <th className="text-left py-1 px-2">Comentario</th>
            <th className="text-center py-1 px-2">Acción</th>
          </tr>
        </thead>
        <tbody>
          {cashouts.length === 0 && (
            <tr><td colSpan={6} className="text-center py-3 text-gray-400 italic">Sin cashouts en esta ventana</td></tr>
          )}
          {cashouts.map(m => (
            <tr key={m.id} className="border-b hover:bg-red-50/30" data-testid={`cashout-row-${m.id}`}>
              <td className="py-1 px-2 whitespace-nowrap">
                {m.date ? new Date(m.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-'}
              </td>
              <td className="text-right py-1 px-2 font-semibold text-red-700">−{fmtMoney(m.amount)}</td>
              <td className="py-1 px-2 max-w-[180px] truncate" title={m.destination_name}>{m.destination_name || '-'}</td>
              <td className="py-1 px-2">
                {m.linked_client_name ? (
                  <span className="font-medium text-indigo-700">{m.linked_client_name}</span>
                ) : (
                  <span className="text-gray-400 italic">sin cliente</span>
                )}
              </td>
              <td className="py-1 px-2">
                {editingId === m.id ? (
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={draftComment}
                      onChange={(e) => setDraftComment(e.target.value)}
                      placeholder="ej: Pago a Pedro"
                      className="border rounded px-2 py-0.5 text-xs flex-1 min-w-[140px]"
                      autoFocus
                      data-testid={`cashout-comment-input-${m.id}`}
                    />
                    <button
                      onClick={() => saveComment(m)}
                      disabled={saving}
                      className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[10px] hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`cashout-comment-save-${m.id}`}
                    >
                      {saving ? '...' : 'OK'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-2 py-0.5 border rounded text-[10px] hover:bg-gray-100"
                    >
                      X
                    </button>
                  </div>
                ) : (
                  <span className={m.admin_comment ? 'text-gray-700' : 'text-gray-400 italic'}>
                    {m.admin_comment || 'sin comentario'}
                  </span>
                )}
              </td>
              <td className="py-1 px-2 text-center">
                {editingId !== m.id && (
                  <button
                    onClick={() => startEdit(m)}
                    data-testid={`cashout-edit-${m.id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded hover:bg-blue-50 text-blue-700 border-blue-300"
                    title="Agregar/editar comentario"
                  >
                    <Pencil className="w-3 h-3" /> {m.admin_comment ? 'Editar' : 'Anotar'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditoriaTab() {
  const todayArgentina = () => {
    const parts = new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date()).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateStr, setDateStr] = useState(todayArgentina);
  const [hours, setHours] = useState('24');
  const [mode, setMode] = useState('date'); // 'date' compara contra banco del día; 'created_at' audita lo subido al CRM
  const [expandUnmatched, setExpandUnmatched] = useState({});
  const [expandOrphans, setExpandOrphans] = useState({});
  const [expandCashouts, setExpandCashouts] = useState({});
  const [selectedCuenta, setSelectedCuenta] = useState(null);  // cuenta abierta en modal
  const [snapshots, setSnapshots] = useState([]);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ date: dateStr, mode });
      if (hours && hours !== '24') params.set('hours', hours);
      const r = await fetch(`${API_URL}/api/audit/astropay?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dateStr, hours, mode]);

  const createSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('mode', mode);
      if (hours && hours !== '24') params.set('hours', hours);
      const r = await fetch(`${API_URL}/api/audit/astropay/snapshot?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      alert(d.alerted
        ? `Snapshot creado y ALERTA enviada (descuadre $${d.descuadre.toLocaleString()})`
        : `Snapshot creado (descuadre $${d.descuadre.toLocaleString()})`);
      if (showSnapshots) loadSnapshots();
    } catch (e) {
      alert('Error creando snapshot: ' + e.message);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const loadSnapshots = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/audit/astropay/snapshots?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setSnapshots(d.snapshots || []);
    } catch (e) {
      console.warn('Snapshots error', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showSnapshots) loadSnapshots(); }, [showSnapshots, loadSnapshots]);

  // Estado de verificacion manual por receipt
  const [verifying, setVerifying] = useState({});  // { receipt_id: true }

  // Modal buscar match para movimiento banco huerfano
  const [matchModal, setMatchModal] = useState(null); // { orphan, account, candidates, loading }
  const [orphanGroupModal, setOrphanGroupModal] = useState(null); // { group, account }

  // Refresh cache worker AstroPay
  const [refreshingCache, setRefreshingCache] = useState(false);

  const refreshAstropayCache = async () => {
    if (refreshingCache) return;
    if (!window.confirm('El refresh consulta AstroPay directamente y puede tardar 1-3 minutos. ¿Continuar?')) return;
    setRefreshingCache(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/audit/astropay/refresh-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) {
        alert('Error refrescando cache: ' + (d.detail || JSON.stringify(d)));
        return;
      }
      const results = d.results || d;
      const msg = Object.entries(results).map(([k, v]) => {
        if (typeof v === 'object' && v !== null) {
          const movs = v.movements !== undefined ? v.movements : (v.new_movements || 0);
          const bal = v.balance !== undefined ? ` · saldo $${Number(v.balance).toLocaleString('es-AR')}` : '';
          return `${k}: ${movs} movs${bal}`;
        }
        return `${k}: ${v}`;
      }).join('\n');
      alert('Cache AstroPay actualizado:\n\n' + msg);
      await load();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setRefreshingCache(false);
    }
  };

  const openFindMatchModal = async (orphan, account) => {
    setMatchModal({ orphan, account, candidates: [], loading: true });
    try {
      const token = localStorage.getItem('token');
      const orphanDate = orphan.date ? new Date(orphan.date).toISOString().slice(0, 10) : dateStr;
      const r = await fetch(`${API_URL}/api/audit/astropay/find-match-for-orphan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date: orphanDate,
          amount: Number(orphan.amount),
          origin_name: orphan.origin_name || '',
          movement_id: orphan.id || '',
          account_id: account.account_id || ''
        })
      });
      const d = await r.json();
      setMatchModal(prev => prev ? ({ ...prev, candidates: d.candidates || [], loading: false, total: d.total_candidates || 0 }) : null);
    } catch (e) {
      setMatchModal(prev => prev ? ({ ...prev, candidates: [], loading: false, error: e.message }) : null);
    }
  };

  const confirmLinkOrphan = async (receipt) => {
    if (!matchModal) return;
    if (!window.confirm(`Vincular receipt ${receipt.reconciliation_id || receipt.id} ($${receipt.amount}) con el movimiento banco de ${matchModal.orphan.origin_name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/audit/astropay/link-orphan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          receipt_id: receipt.id,
          movement_id: matchModal.orphan.id,
          account_name: matchModal.account.name || '',
          admin_note: `Linked from audit: score ${receipt.total_score}`
        })
      });
      const d = await r.json();
      if (d.ok) {
        setMatchModal(null);
        await load();
      } else {
        alert('Error: ' + (d.detail || JSON.stringify(d)));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const forceVerifyReceipt = async (receiptId) => {
    setVerifying(prev => ({ ...prev, [receiptId]: true }));
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/receipts/${receiptId}/force-verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.matched) {
        alert(`✅ MATCH OK\nScore: ${d.score} (${d.quality})\nMovimiento: ${d.movement_id}\nCuenta: ${d.match_type}`);
        await load();  // refrescar tabla
      } else if (d.already_matched) {
        alert(`ℹ️ El receipt ya estaba conciliado.\nMovimiento: ${d.movement_id}`);
        await load();
      } else {
        alert(`❌ No hubo match\n${d.diagnostic || d.message || 'sin datos'}`);
      }
    } catch (e) {
      alert('Error forzando verificacion: ' + e.message);
    } finally {
      setVerifying(prev => ({ ...prev, [receiptId]: false }));
    }
  };

  // Export Excel helpers
  const exportUnmatchedToExcel = (cuenta) => {
    const rows = (cuenta.unmatched_receipts || []).map(r => ({
      Hora: r.time || (r.created_at ? new Date(r.created_at).toLocaleTimeString('es-AR') : '-'),
      Monto: Number(r.amount || 0),
      Origen: r.origin_holder || '-',
      Cliente: r.cliente_name || '-',
      Destino: r.destination_holder || '-',
      Diagnostico: r.diagnostic || r.status || 'not_found',
      ReceiptID: r.id || '',
      Fecha: r.date || dateStr
    }));
    if (!rows.length) return alert('Sin datos para exportar');
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sin conciliar');
    const safeName = (cuenta.name || 'cuenta').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    XLSX.writeFile(wb, `auditoria_unmatched_${safeName}_${dateStr}.xlsx`);
  };

  const exportOrphansToExcel = (cuenta) => {
    const rows = (cuenta.orphan_bank_movs || []).map(m => ({
      Fecha: m.date ? new Date(m.date).toLocaleString('es-AR') : '-',
      Monto: Number(m.amount || 0),
      Origen: m.origin_name || '-',
      Descripcion: m.description || '-',
      MovID: m.id || ''
    }));
    if (!rows.length) return alert('Sin datos para exportar');
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sin comprobante');
    const safeName = (cuenta.name || 'cuenta').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    XLSX.writeFile(wb, `auditoria_orphans_${safeName}_${dateStr}.xlsx`);
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtInt = (n) => Number(n || 0).toLocaleString('es-AR');

  const statusColor = (s) => {
    if (s === 'GREEN' || s === 'OK') return 'text-green-700 bg-green-50 border-green-200';
    if (s === 'YELLOW' || s === 'WARNING') return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };

  const ledger = data?.summary?.bank_ledger || {};
  const recon = data?.summary?.operational_reconciliation || {};
  const timing = data?.summary?.timing || {};
  const orphanGroups = (data?.cuentas || []).flatMap((cuenta) =>
    (cuenta.orphan_groups || []).map((group) => ({
      ...group,
      account_id: cuenta.account_id,
      account_name: cuenta.display_name || cuenta.name,
      account: cuenta,
    }))
  ).sort((a, b) => (b.total || 0) - (a.total || 0));
  const suggestionLabel = (group) => {
    const s = group?.suggested_client;
    if (!s) return 'Sin sugerencia';
    return `${s.cliente_name} (${s.method === 'origin_history' ? 'por origen' : 'por monto'} · ${s.score})`;
  };

  return (
    <div data-testid="auditoria-tab" className="space-y-6">
      {/* Barra de filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fecha</label>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                data-testid="audit-date-picker"
                className="border rounded-md px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Ventana</label>
              <select
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="audit-hours-select"
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                <option value="24">Jornada completa</option>
                <option value="2">Últimas 2 horas</option>
                <option value="4">Últimas 4 horas</option>
                <option value="8">Últimas 8 horas</option>
                <option value="12">Últimas 12 horas</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1" title="Operativa: filtra por cuando el cliente subio el comprobante. Contable: por la fecha real de la transferencia.">Modo</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                data-testid="audit-mode-select"
                className="border rounded-md px-3 py-1.5 text-sm"
              >
                <option value="date">Banco/contable (fecha comprobante)</option>
                <option value="created_at">Operativa (subida al CRM)</option>
              </select>
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="audit-refresh-btn"
              className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={refreshAstropayCache}
              disabled={refreshingCache}
              data-testid="audit-refresh-cache-btn"
              className="flex items-center gap-2 px-4 py-1.5 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700 disabled:opacity-50"
              title="Fuerza al worker a traer movimientos nuevos desde AstroPay (no usa cache)"
            >
              <Zap className={`w-4 h-4 ${refreshingCache ? 'animate-pulse' : ''}`} />
              {refreshingCache ? 'Refrescando cache...' : 'Refrescar cache AstroPay'}
            </button>
            <button
              onClick={createSnapshot}
              disabled={snapshotLoading}
              data-testid="audit-snapshot-btn"
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {snapshotLoading ? 'Guardando...' : 'Snapshot + alerta si descuadre'}
            </button>
            <button
              onClick={() => setShowSnapshots(!showSnapshots)}
              data-testid="audit-history-btn"
              className="flex items-center gap-2 px-4 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50 ml-auto"
            >
              <History className="w-4 h-4" />
              {showSnapshots ? 'Ocultar histórico' : 'Ver histórico'}
            </button>
          </div>
          {data?.period && (
            <div className="text-xs text-gray-500 mt-3">
              Ventana: {new Date(data.period.from).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false })} → {new Date(data.period.to).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false })} <span className="text-gray-400">(hora AR)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          Error: {error}
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="py-4">
                <div className="text-xs text-gray-500">💚 Comprobantes clientes</div>
                <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="kpi-ingresos-receipts">
                  {fmt(data.summary.ingresos_receipts_total)}
                </div>
                <div className="text-xs text-gray-400 mt-1">{fmtInt(data.summary.ingresos_receipts_count)} comprobantes</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-xs text-gray-500">💙 Ingresos banco reales</div>
                <div className="text-2xl font-bold text-blue-700 mt-1" data-testid="kpi-ingresos-banco">
                  {fmt(data.summary.ingresos_banco_total)}
                </div>
                <div className="text-xs text-gray-400 mt-1">{fmtInt(data.summary.ingresos_banco_count)} movimientos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="text-xs text-gray-500">🔴 Pagos/cashouts banco</div>
                <div className="text-2xl font-bold text-red-700 mt-1" data-testid="kpi-cashouts">
                  {fmt(data.summary.cashouts_total)}
                </div>
                <div className="text-xs text-gray-400 mt-1">{fmtInt(data.summary.cashouts_count)} salidas</div>
              </CardContent>
            </Card>
            <Card className={`border-2 ${statusColor(data.summary.status).replace('text-', 'border-')}`}>
              <CardContent className="py-4">
                <div className="text-xs text-gray-500" title="Diferencia entre comprobantes sin impacto y movimientos bancarios sin comprobante">⚖️ Descuadre operativo</div>
                <div className={`text-2xl font-bold mt-1 ${Math.abs(data.summary.descuadre_neto || 0) < 100 ? 'text-green-700' : Math.abs(data.summary.descuadre_neto || 0) < 100000 ? 'text-yellow-700' : 'text-red-700'}`} data-testid="kpi-descuadre">
                  {fmt(data.summary.descuadre_neto)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Status: {data.summary.status}
                  {(data.summary.unmatched_count > 0 || data.summary.orphan_count > 0) && (
                    <span className="ml-1">· {data.summary.unmatched_count || 0} comp. sin match · {data.summary.orphan_count || 0} movs sin comp.</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sugerencias de descuadre */}
          {data.suggestions && data.suggestions.length > 0 && (
            <Card className="border-l-4 border-orange-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Sugerencias del sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.suggestions.map((s, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-md text-sm ${
                      s.severity === 'high'
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                    }`}
                    data-testid={`audit-suggestion-${i}`}
                  >
                    <div className="font-semibold text-xs mb-1">{s.type}</div>
                    <div>{s.message}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Flujo de dinero: banco real separado de conciliacion operativa */}
          {data.summary && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card data-testid="audit-bank-ledger-card" className="border-l-4 border-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    🏦 Libro banco real
                    <span className="text-xs font-normal text-gray-500 ml-auto">Saldo explicado solo con movimientos bancarios</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm font-mono">
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-gray-600 dark:text-gray-300">Saldo banco INICIO</span>
                      <span className="font-semibold">{fmt(ledger.saldo_inicio ?? data.summary.saldo_inicio_dia)}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-blue-700 dark:text-blue-400">+ Ingresos banco reales</span>
                      <span className="font-semibold text-blue-700 dark:text-blue-400">+{fmt(ledger.ingresos_banco_total ?? data.summary.ingresos_banco_total)}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-red-700 dark:text-red-400">− Pagos/cashouts banco</span>
                      <span className="font-semibold text-red-700 dark:text-red-400">−{fmt(ledger.egresos_banco_total ?? data.summary.cashouts_total)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-gray-300 dark:border-gray-600">
                      <span className="font-bold text-gray-900 dark:text-gray-100">Saldo banco ACTUAL</span>
                      <span className="text-lg font-extrabold text-gray-900 dark:text-gray-100">{fmt(ledger.saldo_actual ?? data.summary.saldo_actual)}</span>
                    </div>
                    {Math.abs(ledger.formula_check || 0) > 1 && (
                      <div data-testid="audit-ledger-formula-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        Diferencia de fórmula: {fmt(ledger.formula_check)}. Revisar cache/saldos.
                      </div>
                    )}
                  </div>
                  <div className="mt-3 text-[11px] text-gray-500 italic">
                    Fórmula: saldo inicio + ingresos banco − pagos/cashouts = saldo actual. Los comprobantes no se suman acá.
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="audit-operational-card" className="border-l-4 border-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    ✅ Impacto de comprobantes
                    <span className="text-xs font-normal text-gray-500 ml-auto">
                      Modo: <span className="font-semibold">{mode === 'created_at' ? 'subida al CRM' : 'fecha bancaria/comprobante'}</span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm font-mono">
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-emerald-700 dark:text-emerald-400">Comprobantes del período</span>
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">{fmt(recon.receipts_total ?? data.summary.ingresos_receipts_total)}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-violet-700 dark:text-violet-400">Impactaron en banco</span>
                      <span className="font-semibold text-violet-700 dark:text-violet-400">{fmt(recon.impactado_en_banco_total ?? data.summary.matched_receipts_total)}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-yellow-700 dark:text-yellow-400">Pendiente: comp. sin impacto</span>
                      <span className="font-semibold text-yellow-700 dark:text-yellow-400">{fmt(recon.pendiente_impacto_total ?? data.summary.unmatched_total)}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-700 py-1">
                      <span className="text-orange-700 dark:text-orange-400">Banco sin comprobante</span>
                      <span className="font-semibold text-orange-700 dark:text-orange-400">{fmt(recon.banco_sin_comprobante_total ?? data.summary.orphan_total)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-gray-300 dark:border-gray-600">
                      <span className="font-bold text-gray-900 dark:text-gray-100">Descuadre operativo</span>
                      <span className={`text-lg font-extrabold ${Math.abs(data.summary.descuadre_neto || 0) < 100 ? 'text-green-700' : Math.abs(data.summary.descuadre_neto || 0) < 100000 ? 'text-yellow-700' : 'text-red-700'}`}>{fmt(recon.descuadre_operativo ?? data.summary.descuadre_neto)}</span>
                    </div>
                  </div>
                  {(timing.backdated_receipts_count || 0) > 0 && (
                    <div data-testid="audit-backdated-warning" className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      {timing.backdated_receipts_count} comprobante(s) del período fueron subidos tarde por {fmt(timing.backdated_receipts_total)}. Cambiá a “Operativa” si querés auditar carga del CRM.
                    </div>
                  )}
                  <div className="mt-3 text-[11px] text-gray-500 italic">
                    Descuadre operativo = comprobantes sin match − movimientos banco sin comprobante.
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {orphanGroups.length > 0 && (
            <Card data-testid="audit-orphan-groups-card" className="border-l-4 border-orange-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  🧾 Banco sin comprobante agrupado
                  <span className="text-xs font-normal text-gray-500 ml-auto">
                    Posibles clientes que pagaron sin avisar
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 border-b">
                      <tr>
                        <th className="text-left py-2 px-2">Origen</th>
                        <th className="text-right py-2 px-2">Monto</th>
                        <th className="text-right py-2 px-2">Cant.</th>
                        <th className="text-right py-2 px-2">Total</th>
                        <th className="text-left py-2 px-2">Cuenta</th>
                        <th className="text-left py-2 px-2">Cliente sugerido</th>
                        <th className="text-left py-2 px-2">Último</th>
                        <th className="text-right py-2 px-2">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanGroups.slice(0, 12).map((group) => (
                        <tr key={`${group.account_id}-${group.key}`} data-testid={`audit-orphan-group-${group.account_id}-${group.key}`} className="border-b hover:bg-orange-50/50">
                          <td className="py-2 px-2 font-medium text-gray-900">{group.origin_name || 'Sin origen'}</td>
                          <td className="py-2 px-2 text-right font-semibold text-orange-700">{fmt(group.amount)}</td>
                          <td className="py-2 px-2 text-right">{group.count}</td>
                          <td className="py-2 px-2 text-right font-bold text-orange-700">{fmt(group.total)}</td>
                          <td className="py-2 px-2 text-xs text-gray-600 max-w-[220px] truncate">{group.account_name}</td>
                          <td className="py-2 px-2 text-xs">
                            {group.suggested_client ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                                {suggestionLabel(group)}
                              </span>
                            ) : (
                              <span className="text-gray-400">Sin sugerencia</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-xs text-gray-500">
                            {group.last_date ? new Date(group.last_date).toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <button
                              data-testid={`audit-orphan-group-view-${group.account_id}-${group.key}`}
                              onClick={() => setOrphanGroupModal({ group, account: group.account })}
                              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                            >
                              Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {orphanGroups.length > 12 && (
                  <div className="mt-2 text-xs text-gray-500">Mostrando top 12 por monto total. El detalle completo sigue disponible por cuenta.</div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tabla de cuentas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Flujo por cuenta AstroPay</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="text-left py-2 px-2">Cuenta</th>
                    <th className="text-right py-2 px-2">Comprobantes</th>
                    <th className="text-right py-2 px-2">Ingresos banco</th>
                    <th className="text-right py-2 px-2" title="Diferencia informativa entre comprobantes del período y movimientos banco. Puede variar por comprobantes subidos tarde.">Δ informativo</th>
                    <th className="text-right py-2 px-2">Pagos banco</th>
                    <th className="text-right py-2 px-2 text-gray-400" title="Calculado retroactivo, NO real. Solo orientativo.">Saldo Apert. <span className="text-[9px]">(calc)</span></th>
                    <th className="text-right py-2 px-2">Saldo Actual</th>
                    <th className="text-right py-2 px-2" title="Descuadre operativo: comprobantes sin impacto − movimientos banco sin comprobante">Descuadre operativo</th>
                    <th className="text-center py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cuentas.map((c) => (
                    <tr
                      key={c.account_id}
                      className="border-b hover:bg-emerald-50 dark:hover:bg-emerald-900/20 cursor-pointer transition-colors"
                      onClick={() => setSelectedCuenta(c)}
                      data-testid={`audit-row-${c.account_id}`}
                      title="Click para ver detalle completo"
                    >
                      <td className="py-2 px-2">
                        <div className="font-medium">{c.display_name || c.name}</div>
                        <div className="text-xs text-gray-500">{c.titular}</div>
                        {c.astropay_fallback && (
                          <div className="text-[10px] text-amber-600">⚠️ datos desde cache AstroPay</div>
                        )}
                      </td>
                      <td className="text-right py-2 px-2">
                        <div className="font-medium text-emerald-700">{fmt(c.ingresos_receipts.total)}</div>
                        <div className="text-xs text-gray-400">{c.ingresos_receipts.count} · impactados {c.operational_reconciliation?.matched_receipts_count ?? '-'}</div>
                      </td>
                      <td className="text-right py-2 px-2">
                        <div className="font-medium text-blue-700">{fmt(c.ingresos_banco.total)}</div>
                        <div className="text-xs text-gray-400">{c.ingresos_banco.count}</div>
                      </td>
                      <td className={`text-right py-2 px-2 font-semibold ${Math.abs(c.delta_ingresos) < 1 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmt(c.delta_ingresos)}
                      </td>
                      <td className="text-right py-2 px-2">
                        <div className="font-medium text-red-700">{fmt(c.cashouts.total)}</div>
                        <div className="text-xs text-gray-400">{c.cashouts.count}</div>
                      </td>
                      <td className="text-right py-2 px-2 text-gray-400 italic" title="Calculado retroactivo - solo informativo">{fmt(c.saldo_apertura)}</td>
                      <td className="text-right py-2 px-2 font-medium">{fmt(c.saldo_actual)}</td>
                      <td className={`text-right py-2 px-2 font-semibold ${Math.abs(c.descuadre || 0) < 100 ? 'text-green-600' : Math.abs(c.descuadre || 0) < 50000 ? 'text-yellow-600' : 'text-red-600'}`} title={`unmatched ${fmt(c.unmatched_receipts_total)} − orphans ${fmt(c.orphan_bank_movs_total)}`}>
                        {fmt(c.descuadre)}
                      </td>
                      <td className="text-center py-2 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${statusColor(c.status)}`}>
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Acordeones por cuenta */}
          <div className="space-y-3">
            {data.cuentas.map((c) => (
              <div key={c.account_id + '_detail'} className="space-y-2">
                {/* Receipts sin match */}
                {c.unmatched_receipts_count > 0 && (
                  <Card className="border-l-4 border-yellow-400">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <div
                          className="flex items-center gap-2 cursor-pointer flex-1"
                          onClick={() => setExpandUnmatched(prev => ({ ...prev, [c.account_id]: !prev[c.account_id] }))}
                        >
                          {expandUnmatched[c.account_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <AlertTriangle className="w-4 h-4 text-yellow-600" />
                          <span className="font-semibold">{c.unmatched_receipts_count} comprobantes sin conciliar</span>
                          <span className="text-gray-500">· {fmt(c.unmatched_receipts_total)}</span>
                          <span className="ml-auto text-xs text-gray-400">{c.name}</span>
                        </div>
                        <button
                          data-testid={`export-unmatched-${c.account_id}`}
                          onClick={(e) => { e.stopPropagation(); exportUnmatchedToExcel(c); }}
                          className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-green-50 text-green-700 border-green-300"
                          title="Exportar a Excel"
                        >
                          <FileSpreadsheet className="w-3 h-3" /> Excel
                        </button>
                      </div>
                    </CardHeader>
                    {expandUnmatched[c.account_id] && (
                      <CardContent className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 border-b">
                              <th className="text-left py-1 px-2">Subido</th>
                              <th className="text-left py-1 px-2">Fecha comp.</th>
                              <th className="text-right py-1 px-2">Monto</th>
                              <th className="text-left py-1 px-2">Origen</th>
                              <th className="text-left py-1 px-2">Cliente</th>
                              <th className="text-left py-1 px-2">Destino</th>
                              <th className="text-left py-1 px-2">Diagnóstico</th>
                              <th className="text-center py-1 px-2">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.unmatched_receipts.map(r => (
                              <tr key={r.id} className="border-b hover:bg-yellow-50">
                                <td className="py-1 px-2">{r.time || (r.created_at ? new Date(r.created_at).toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-')}</td>
                                <td className="py-1 px-2">
                                  {r.date || '-'}
                                  {r.lag_days > 0 && (
                                    <span
                                      className={`ml-1 inline-block px-1 py-0.5 rounded text-[9px] font-bold ${r.lag_days <= 2 ? 'bg-yellow-100 text-yellow-800' : r.lag_days <= 7 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}
                                      title={`Subido ${r.lag_days} día(s) después de la fecha del comprobante`}
                                    >
                                      +{r.lag_days}d
                                    </span>
                                  )}
                                </td>
                                <td className="text-right py-1 px-2 font-semibold">{fmt(r.amount)}</td>
                                <td className="py-1 px-2">{r.origin_holder || '-'}</td>
                                <td className="py-1 px-2 font-medium text-indigo-700">{r.cliente_name || '-'}</td>
                                <td className="py-1 px-2">{r.destination_holder || '-'}</td>
                                <td className="py-1 px-2 text-gray-500">{r.diagnostic || r.status || 'not_found'}</td>
                                <td className="py-1 px-2 text-center">
                                  <button
                                    data-testid={`verify-receipt-${r.id}`}
                                    onClick={() => forceVerifyReceipt(r.id)}
                                    disabled={verifying[r.id]}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded hover:bg-blue-50 text-blue-700 border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Forzar verificación manual contra el worker"
                                  >
                                    {verifying[r.id] ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="w-3 h-3" />
                                    )}
                                    {verifying[r.id] ? 'Verificando...' : 'Verificar'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Movimientos banco sin receipt */}
                {c.orphan_bank_movs_count > 0 && (
                  <Card className="border-l-4 border-blue-400">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <div
                          className="flex items-center gap-2 cursor-pointer flex-1"
                          onClick={() => setExpandOrphans(prev => ({ ...prev, [c.account_id]: !prev[c.account_id] }))}
                        >
                          {expandOrphans[c.account_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold">{c.orphan_bank_movs_count} movimientos banco sin comprobante</span>
                          <span className="text-gray-500">· {fmt(c.orphan_bank_movs_total)}</span>
                          <span className="ml-auto text-xs text-gray-400">{c.name}</span>
                        </div>
                        <button
                          data-testid={`export-orphans-${c.account_id}`}
                          onClick={(e) => { e.stopPropagation(); exportOrphansToExcel(c); }}
                          className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-green-50 text-green-700 border-green-300"
                          title="Exportar a Excel"
                        >
                          <FileSpreadsheet className="w-3 h-3" /> Excel
                        </button>
                      </div>
                    </CardHeader>
                    {expandOrphans[c.account_id] && (
                      <CardContent className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 border-b">
                              <th className="text-left py-1 px-2">Fecha</th>
                              <th className="text-right py-1 px-2">Monto</th>
                              <th className="text-left py-1 px-2">Origen</th>
                              <th className="text-left py-1 px-2">Descripción</th>
                              <th className="text-center py-1 px-2">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.orphan_bank_movs.map(m => (
                              <tr key={m.id} className="border-b hover:bg-blue-50">
                                <td className="py-1 px-2">{m.date ? new Date(m.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-'}</td>
                                <td className="text-right py-1 px-2 font-semibold">{fmt(m.amount)}</td>
                                <td className="py-1 px-2">{m.origin_name || '-'}</td>
                                <td className="py-1 px-2 text-gray-500">{m.description || '-'}</td>
                                <td className="py-1 px-2 text-center">
                                  <button
                                    data-testid={`find-match-orphan-${m.id}`}
                                    onClick={() => openFindMatchModal(m, c)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded hover:bg-blue-50 text-blue-700 border-blue-300"
                                    title="Buscar receipts candidatos para este movimiento"
                                  >
                                    <Search className="w-3 h-3" /> Buscar match
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Cashouts (debits del banco) - desplegable con cliente vinculado y comentario */}
                {(c.cashouts?.count > 0) && (
                  <Card className="border-l-4 border-red-400">
                    <CardHeader className="pb-2">
                      <div
                        className="flex items-center gap-2 text-sm cursor-pointer"
                        onClick={() => setExpandCashouts(prev => ({ ...prev, [c.account_id]: !prev[c.account_id] }))}
                      >
                        {expandCashouts[c.account_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <ArrowRightLeft className="w-4 h-4 text-red-600" />
                        <span className="font-semibold">{c.cashouts.count} cashouts</span>
                        <span className="text-gray-500">· {fmt(c.cashouts.total)}</span>
                        <span className="ml-auto text-xs text-gray-400">{c.name}</span>
                      </div>
                    </CardHeader>
                    {expandCashouts[c.account_id] && (
                      <CardContent className="overflow-x-auto">
                        <CashoutsTable cuenta={c} dateStr={dateStr} onUpdate={load} />
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            ))}
          </div>

          {/* Histórico de snapshots */}
          {showSnapshots && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Histórico de auditorías
                </CardTitle>
              </CardHeader>
              <CardContent>
                {snapshots.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Sin snapshots aún. Creá uno con el botón "Snapshot".</p>
                ) : (
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="text-left py-1 px-2">Fecha</th>
                        <th className="text-right py-1 px-2">Receipts</th>
                        <th className="text-right py-1 px-2">Banco</th>
                        <th className="text-right py-1 px-2">Cashouts</th>
                        <th className="text-right py-1 px-2">Descuadre</th>
                        <th className="text-center py-1 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshots.map(s => (
                        <tr key={s.id} className="border-b">
                          <td className="py-1 px-2">{new Date(s.created_at).toLocaleString('es-AR')}</td>
                          <td className="text-right py-1 px-2">{fmt(s.summary?.ingresos_receipts_total)}</td>
                          <td className="text-right py-1 px-2">{fmt(s.summary?.ingresos_banco_total)}</td>
                          <td className="text-right py-1 px-2">{fmt(s.summary?.cashouts_total)}</td>
                          <td className={`text-right py-1 px-2 font-semibold ${Math.abs(s.summary?.descuadre_neto || 0) < 1 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmt(s.summary?.descuadre_neto)}
                          </td>
                          <td className="text-center py-1 px-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusColor(s.summary?.status)}`}>
                              {s.summary?.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal: Detalle de cuenta (Receipts / Movs Banco / Cashouts) */}
      {selectedCuenta && (
        <CuentaDetailModal
          cuenta={selectedCuenta}
          dateStr={dateStr}
          onClose={() => setSelectedCuenta(null)}
          onUpdate={load}
        />
      )}

      {/* Modal: Grupo de movimientos banco sin comprobante */}
      {orphanGroupModal && (
        <div data-testid="audit-orphan-group-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setOrphanGroupModal(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  Banco sin comprobante: {orphanGroupModal.group.origin_name || 'Sin origen'} · {fmt(orphanGroupModal.group.amount)}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {orphanGroupModal.group.count} movimiento(s) · Total {fmt(orphanGroupModal.group.total)} · {orphanGroupModal.group.account_name}
                </p>
              </div>
              <button data-testid="audit-orphan-group-close-button" onClick={() => setOrphanGroupModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-bold">Cliente posiblemente omitió comprobante</div>
                {orphanGroupModal.group.suggested_client ? (
                  <div data-testid="audit-orphan-group-suggested-client" className="mt-1 text-sm text-emerald-900">
                    <span className="font-bold">{orphanGroupModal.group.suggested_client.cliente_name}</span>
                    <span className="ml-2 text-xs text-emerald-700">
                      sugerido {orphanGroupModal.group.suggested_client.method === 'origin_history' ? 'por historial del titular origen' : 'por historial de montos'} · score {orphanGroupModal.group.suggested_client.score}
                    </span>
                  </div>
                ) : (
                  <div data-testid="audit-orphan-group-no-suggestion" className="mt-1 text-sm text-gray-600">
                    Sin sugerencia firme. Revisar WhatsApp por monto/hora o usar “Buscar comprobante”.
                  </div>
                )}
                {(orphanGroupModal.group.suggested_clients || []).length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {orphanGroupModal.group.suggested_clients.slice(1).map(s => (
                      <span key={s.cliente_id} className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700">
                        Alternativa: {s.cliente_name} · {s.score}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-2">Hora</th>
                      <th className="text-right py-2 px-2">Monto</th>
                      <th className="text-left py-2 px-2">Origen</th>
                      <th className="text-left py-2 px-2">Descripción</th>
                      <th className="text-left py-2 px-2">Sugerencia</th>
                      <th className="text-right py-2 px-2">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orphanGroupModal.group.movements || []).map(m => (
                      <tr key={m.id || `${m.date}-${m.amount}`} data-testid={`audit-orphan-group-movement-${m.id || m.date}`} className="border-b hover:bg-orange-50/50">
                        <td className="py-2 px-2 text-xs text-gray-600">
                          {m.date ? new Date(m.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-'}
                        </td>
                        <td className="py-2 px-2 text-right font-semibold text-orange-700">{fmt(m.amount)}</td>
                        <td className="py-2 px-2">{m.origin_name || '-'}</td>
                        <td className="py-2 px-2 text-xs text-gray-500 max-w-[260px] truncate">{m.description || '-'}</td>
                        <td className="py-2 px-2 text-xs">
                          {m.probable_client_name ? (
                            <span className="font-semibold text-emerald-700">{m.probable_client_name}</span>
                          ) : (
                            <span className="text-gray-400">Sin sugerencia</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <button
                            data-testid={`audit-orphan-group-find-match-${m.id || 'no-id'}`}
                            onClick={() => {
                              const account = orphanGroupModal.account;
                              setOrphanGroupModal(null);
                              openFindMatchModal(m, account);
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                          >
                            Buscar comprobante
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Buscar match para movimiento huerfano */}
      {matchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMatchModal(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  Candidatos de match
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Movimiento banco: <strong>{fmt(matchModal.orphan.amount)}</strong> · {matchModal.orphan.origin_name || '(sin origen)'} · {matchModal.orphan.date ? new Date(matchModal.orphan.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false }) : '-'}
                </p>
              </div>
              <button onClick={() => setMatchModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {matchModal.loading ? (
                <div className="text-center py-8 text-gray-500">
                  <RefreshCw className="w-6 h-6 animate-spin inline-block mr-2" /> Buscando candidatos...
                </div>
              ) : matchModal.candidates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No se encontraron receipts candidatos con monto similar.</p>
                  <p className="text-xs mt-2">El receipt puede haberse subido sin `destination_account` o con monto muy distinto.</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    {matchModal.total} candidatos encontrados (mostrando top {matchModal.candidates.length}). Confirmá cuál corresponde:
                  </p>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b text-xs">
                        <th className="text-left py-2 px-2">ID</th>
                        <th className="text-right py-2 px-2">Monto</th>
                        <th className="text-left py-2 px-2">Cliente</th>
                        <th className="text-left py-2 px-2">Origen</th>
                        <th className="text-center py-2 px-2">Score</th>
                        <th className="text-center py-2 px-2">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchModal.candidates.map(r => {
                        const scoreColor = r.total_score >= 80 ? 'bg-green-100 text-green-700' :
                                          r.total_score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-red-100 text-red-700';
                        return (
                          <tr key={r.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-2 font-mono text-xs">{r.reconciliation_id || r.id.slice(0, 8)}</td>
                            <td className="text-right py-2 px-2 font-semibold">{fmt(r.amount)}</td>
                            <td className="py-2 px-2 text-indigo-700">{r.cliente_name}</td>
                            <td className="py-2 px-2">{r.origin_holder || '-'}</td>
                            <td className="text-center py-2 px-2">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${scoreColor}`}>
                                {r.total_score}
                              </span>
                              <div className="text-[10px] text-gray-400">n:{r.name_score} · $:{r.amount_score}</div>
                            </td>
                            <td className="text-center py-2 px-2">
                              <button
                                data-testid={`link-orphan-${r.id}`}
                                onClick={() => confirmLinkOrphan(r)}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs border rounded hover:bg-green-50 text-green-700 border-green-300 font-medium"
                              >
                                <Check className="w-3 h-3" /> Vincular
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Wrapper TotalCoin embebido en Bancos ========== */
function TotalCoinEmbedded() {
  const token = localStorage.getItem('token');
  return <TotalCoin token={token} embedded />;
}

/* ========== Componente Principal ========== */
/* ========== Tab: Conexión Bot (multibot) ========== */
function BotCard({ bot, onAction, onDelete }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [busy, setBusy] = useState('');
  const [pairPhone, setPairPhone] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [pairing, setPairing] = useState(false);
  const [showPair, setShowPair] = useState(false);
  // El QR de WhatsApp vence cada ~20s y Baileys emite uno nuevo, pero /status no
  // cambia (sigue en "waiting"), así que sin este tick la imagen quedaba vieja y
  // el escaneo fallaba. (03/09/2026)
  const [qrTick, setQrTick] = useState(0);
  const token = localStorage.getItem('token');
  const isConnected = bot.status === 'connected';
  const isWaiting = bot.status === 'waiting' || bot.has_qr;

  useEffect(() => {
    if (isConnected || !bot.online) return;
    const iv = setInterval(() => setQrTick((t) => t + 1), 15000);
    return () => clearInterval(iv);
  }, [isConnected, bot.online]);

  const getPairCode = async () => {
    const phone = pairPhone.replace(/[^0-9]/g, '');
    if (phone.length < 10) { alert('Ingresá el número en formato internacional sin +, ej: 5493510517958'); return; }
    setPairing(true); setPairCode('');
    try {
      const res = await fetch(`${API_URL}/api/bancos/bots/${bot.id}/pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });
      const d = await res.json();
      if (res.ok && d.code) setPairCode(d.code);
      else alert(`No se pudo generar el código: ${d.detail || res.status}`);
    } catch (e) { alert('Error de red al generar el código'); } finally { setPairing(false); }
  };

  // Cargar el QR como blob (el endpoint requiere Authorization header)
  useEffect(() => {
    let revoke = null;
    if (!isConnected && (isWaiting || bot.online)) {
      (async () => {
        try {
          const res = await fetch(`${API_URL}/api/bancos/bots/${bot.id}/qr`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            revoke = url;
            setQrUrl(url);
          } else { setQrUrl(null); }
        } catch (e) { setQrUrl(null); }
      })();
    } else { setQrUrl(null); }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
    // eslint-disable-next-line
  }, [bot.status, bot.has_qr, bot.online, qrTick]);

  const act = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(action);
    await onAction(bot, action);
    setBusy('');
  };

  const dot = isConnected ? 'bg-emerald-500' : isWaiting ? 'bg-amber-400' : 'bg-red-500';
  const statusLabel = isConnected ? 'Conectado' : isWaiting ? 'Esperando QR' : (bot.online ? bot.status : 'Detenido');

  return (
    <Card className="border border-gray-200 dark:border-gray-700" data-testid={`bot-card-${bot.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isConnected ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <Smartphone className={`w-5 h-5 ${isConnected ? 'text-emerald-600' : 'text-gray-500'}`} />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">{bot.label}
                {!bot.manageable && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">AUTOGESTIONADO</span>}
              </CardTitle>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`w-2 h-2 rounded-full ${dot} ${isConnected ? 'animate-pulse' : ''}`} />
                <span className="text-xs text-gray-500" data-testid={`bot-status-${bot.id}`}>{statusLabel}</span>
              </div>
            </div>
          </div>
          {!bot.seeded && onDelete && (
            <button
              type="button"
              onClick={() => onDelete(bot)}
              title="Eliminar bot"
              data-testid={`bot-delete-${bot.id}`}
              className="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mb-3">
          <div className="flex justify-between"><span>Número</span><span className="font-mono font-semibold text-gray-900 dark:text-gray-100" data-testid={`bot-number-${bot.id}`}>{bot.number ? `+${bot.number}` : '—'}</span></div>
          {bot.name && <div className="flex justify-between"><span>Nombre</span><span className="font-medium">{bot.name}</span></div>}
          {bot.queue && <div className="flex justify-between"><span>Cola de mensajes</span><span className="font-medium">{bot.queue.pending ?? bot.queue.size ?? 0}</span></div>}
          <div className="flex justify-between"><span>Proceso</span><span className="font-mono text-[10px]">{bot.supervisor_program}</span></div>
        </div>

        {/* QR para vincular */}
        {!isConnected && qrUrl && (
          <div className="flex flex-col items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 mb-3" data-testid={`bot-qr-${bot.id}`}>
            <img src={qrUrl} alt="QR" className="w-44 h-44" />
            <p className="text-[11px] text-gray-500 text-center">Escaneá con WhatsApp → Dispositivos vinculados</p>
          </div>
        )}

        {/* Vincular con CÓDIGO (sin QR) — ideal para cloudphone. El principal no lo soporta. */}
        {!isConnected && bot.online && bot.manageable && (
          <div className="mb-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40" data-testid={`bot-pair-box-${bot.id}`}>
            {!showPair ? (
              <button onClick={() => setShowPair(true)} className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-medium" data-testid={`bot-pair-toggle-${bot.id}`}>
                <Link2 className="w-3.5 h-3.5" /> Vincular con código (sin QR)
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] text-gray-600 dark:text-gray-400">Ingresá el número del WhatsApp ya activado (formato internacional sin +):</p>
                <div className="flex gap-2">
                  <input value={pairPhone} onChange={(e) => setPairPhone(e.target.value)} placeholder="5493510517958"
                    className="flex-1 text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 font-mono" data-testid={`bot-pair-input-${bot.id}`} />
                  <button onClick={getPairCode} disabled={pairing} className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50" data-testid={`bot-pair-submit-${bot.id}`}>
                    {pairing ? '…' : 'Obtener'}
                  </button>
                </div>
                {pairCode && (
                  <div className="flex flex-col items-center gap-1 p-3 bg-white dark:bg-gray-900 rounded-lg border-2 border-indigo-300" data-testid={`bot-pair-code-${bot.id}`}>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">Código de vinculación</span>
                    <span className="text-2xl font-mono font-bold tracking-[0.2em] text-indigo-700 dark:text-indigo-300">{pairCode}</span>
                    <p className="text-[10px] text-gray-500 text-center mt-1">En el teléfono: WhatsApp → Dispositivos vinculados → Vincular dispositivo → <b>Vincular con número de teléfono</b> → ingresá este código.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Acciones */}
        {bot.manageable ? (
          <div className="grid grid-cols-2 gap-2">
            {!bot.online ? (
              <button onClick={() => act('start')} disabled={!!busy} className="col-span-2 flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50" data-testid={`bot-start-${bot.id}`}>
                <Play className="w-3.5 h-3.5" />{busy === 'start' ? 'Iniciando…' : 'Iniciar y vincular'}
              </button>
            ) : (
              <>
                <button onClick={() => act('relink', `¿Re-vincular "${bot.label}"? Se borrará la sesión actual y se generará un QR nuevo para escanear.`)} disabled={!!busy} className="flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50" data-testid={`bot-relink-${bot.id}`}>
                  <Link2 className="w-3.5 h-3.5" />{busy === 'relink' ? '…' : 'Re-vincular'}
                </button>
                <button onClick={() => act('restart', `¿Reiniciar "${bot.label}"?`)} disabled={!!busy} className="flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white font-medium disabled:opacity-50" data-testid={`bot-restart-${bot.id}`}>
                  <RefreshCw className="w-3.5 h-3.5" />{busy === 'restart' ? '…' : 'Reiniciar'}
                </button>
                <button onClick={() => act('stop', `¿Detener "${bot.label}"? Dejará de estar disponible como respaldo.`)} disabled={!!busy} className="col-span-2 flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-700 dark:text-gray-200 font-medium disabled:opacity-50" data-testid={`bot-stop-${bot.id}`}>
                  <Square className="w-3.5 h-3.5" />{busy === 'stop' ? '…' : 'Detener'}
                </button>
              </>
            )}
          </div>
        ) : isConnected ? (
          <p className="text-[11px] text-gray-400 text-center py-2">Este bot se reconecta y reinicia solo (watchdog). No requiere acciones manuales.</p>
        ) : (
          <div className="space-y-2" data-testid={`bot-caido-${bot.id}`}>
            <p className="text-[11px] text-amber-600 dark:text-amber-400 text-center leading-snug">
              La sesión está caída. WhatsApp la cierra cuando el número se vincula en otro
              dispositivo, y el bot no puede reconectar solo: hay que escanear un QR nuevo.
            </p>
            <button
              onClick={() => act('relink', `¿Generar un QR nuevo para "${bot.label}"?\n\nSe borra la sesión actual y el bot se reinicia. Tené el teléfono a mano: WhatsApp → Dispositivos vinculados → Vincular dispositivo.`)}
              disabled={!!busy}
              className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
              data-testid={`bot-relink-${bot.id}`}
            >
              <Link2 className="w-3.5 h-3.5" />{busy === 'relink' ? 'Generando…' : 'Generar QR nuevo'}
            </button>
            <p className="text-[10px] text-gray-400 text-center">
              El QR aparece acá en unos segundos y se refresca solo.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConexionBotTab() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newProxy, setNewProxy] = useState('');
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem('token');

  const fetchBots = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bancos/bots`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setBots(d.bots || []); }
    } catch (e) { console.error('fetch bots error', e); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchBots();
    const iv = setInterval(fetchBots, 5000); // estado en vivo + refresco del QR
    return () => clearInterval(iv);
  }, [fetchBots]);

  const handleAction = async (bot, action) => {
    try {
      const res = await fetch(`${API_URL}/api/bancos/bots/${bot.id}/${action}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert(`No se pudo ${action}: ${e.detail || res.status}`); }
      setTimeout(fetchBots, 1500);
    } catch (e) { alert(`Error de red al ejecutar ${action}`); }
  };

  const handleDelete = async (bot) => {
    if (!window.confirm(`¿Eliminar "${bot.label}" definitivamente?\n\nSe detendrá el proceso, se borrará su sesión de WhatsApp y se removerá de la lista. Esta acción es irreversible.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/bancos/bots/${bot.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert(`No se pudo eliminar: ${e.detail || res.status}`); }
      else setTimeout(fetchBots, 800);
    } catch (e) { alert('Error de red al eliminar'); }
  };

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) { alert('Ingresá un nombre para el bot'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/bots/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label, ws_proxy: newProxy.trim() || null }),
      });
      const d = await res.json();
      if (res.ok) {
        setShowCreate(false); setNewLabel(''); setNewProxy('');
        setTimeout(fetchBots, 500);
      } else {
        alert(`No se pudo crear el bot: ${d.detail || res.status}`);
      }
    } catch (e) { alert('Error de red al crear el bot'); } finally { setCreating(false); }
  };

  return (
    <div data-testid="conexion-bot-tab">
      <div className="mb-5 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 flex gap-3">
        <QrCode className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <p className="font-semibold mb-1">Bot de WhatsApp para Administración</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Cada bot corre como un proceso independiente con su propia sesión, puerto y número de WhatsApp (aislamiento total). El <b>Bot Principal</b> se autogestiona; los bots de respaldo o agencias se inician/vinculan manualmente desde acá. Usá <b>+ Agregar Bot</b> para crear instancias adicionales (quedan en <i>standby</i> hasta que las inicies).</p>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando bots…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {bots.map(bot => <BotCard key={bot.id} bot={bot} onAction={handleAction} onDelete={handleDelete} />)}
          {/* Tarjeta "+ Agregar Bot" */}
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="bot-add-card"
            className="group flex flex-col items-center justify-center gap-3 min-h-[240px] rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 hover:border-emerald-500 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10 transition-colors cursor-pointer"
          >
            <div className="w-14 h-14 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center group-hover:border-emerald-500 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 transition-colors">
              <Plus className="w-7 h-7 text-gray-400 group-hover:text-emerald-600 transition-colors" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 group-hover:text-emerald-700">Agregar Bot</p>
              <p className="text-xs text-gray-500 mt-0.5">Crear nueva instancia en standby</p>
            </div>
          </button>
        </div>
      )}

      {/* Modal crear bot */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bot-create-modal">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Nuevo Bot de WhatsApp</h3>
              <button onClick={() => { setShowCreate(false); setNewLabel(''); setNewProxy(''); }} className="text-gray-400 hover:text-gray-600" data-testid="bot-create-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">El bot se creará en <b>standby</b> (proceso registrado pero detenido). Cuando estés listo, tocá <b>Iniciar y vincular</b> en su tarjeta para arrancar y escanear el QR.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Nombre del bot *</label>
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Ej: Bot Agencia Córdoba"
                  maxLength={60}
                  data-testid="bot-create-label"
                  className="w-full text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-[10px] text-gray-400 mt-1">Se generará un identificador automáticamente. Solo letras y números.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Proxy WebSocket <span className="text-gray-400">(opcional)</span></label>
                <input
                  value={newProxy}
                  onChange={(e) => setNewProxy(e.target.value)}
                  placeholder="URL del proxy"
                  data-testid="bot-create-proxy"
                  className="w-full text-xs px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-[10px] text-gray-400 mt-1">Dejá vacío si no necesitás proxy. Útil para asignar IP residencial por bot/agencia.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => { setShowCreate(false); setNewLabel(''); setNewProxy(''); }}
                className="flex-1 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                data-testid="bot-create-cancel"
              >Cancelar</button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="bot-create-submit"
              >{creating ? 'Creando…' : 'Crear en Standby'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Bancos() {
  const [activeTab, setActiveTab] = useState('cuentas');
  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="bancos-module">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2"><Landmark className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Bancos</h1></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Gestion de cuentas bancarias, movimientos y alertas</p>
        </div>
        
      </div>
      <div className="flex flex-wrap gap-1 mb-8 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit" data-testid="bancos-tabs">
        {TABS.map(tab => { const Icon = tab.icon; const active = activeTab === tab.id;
          return (<button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`bancos-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${active ? 'bg-white dark:bg-gray-700 text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <Icon className="w-4 h-4" />{tab.label}</button>);})}
      </div>
      {activeTab === 'cuentas' && <CuentasTab />}
      {activeTab === 'movimientos' && <MovimientosTab />}
      {activeTab === 'totalcoin' && <TotalCoinEmbedded />}
      {activeTab === 'automatizacion' && <AutomatizacionTab />}
      {activeTab === 'conexion-bot' && <ConexionBotTab />}
      {activeTab === 'resumen' && <ResumenEjecutivoTab />}
      {activeTab === 'auditoria' && <AuditoriaTab />}{activeTab === 'android' && <AndroidNoticesTab />}
      {activeTab === 'alertas' && (<Card className="border-dashed border-2 border-gray-300"><CardHeader className="text-center pb-2">
        <Construction className="w-12 h-12 text-gray-400 mx-auto mb-3" /><CardTitle className="text-lg text-gray-500">Alertas</CardTitle></CardHeader>
        <CardContent className="text-center pb-8"><p className="text-gray-400 text-sm">Proximamente</p></CardContent></Card>)}
    </div>
  );
}
